import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useNavigate, useParams } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { FreshnessBadge } from '../lib/metaFreshness.jsx';
import { formatUsdCents } from '../lib/formatters.js';
import { formatStamp } from '../lib/timeFields.jsx';
import { useVenueTz } from '../lib/venueTz.js';
import TrendChart from '../lib/TrendChart.jsx';
import AudienceMap from './widget/AudienceMap.jsx';
import DeviceEvidence, { EvidenceBadges, EvidenceDrawer } from './widget/DeviceEvidence.jsx';

// Funnel stages drawn on the daily trend charts. Colors are Tailwind text
// classes — TrendChart strokes with currentColor.
const TREND_STAGES = [
  // page_views only accrues from when host-page beaconing shipped (June 2026)
  // — earlier days plot flat 0; the legend toggle hides the line if noisy.
  { key: 'page_views',      label: 'Page views',      className: 'text-slate-400' },
  { key: 'opens',           label: 'Opens',           className: 'text-sky-500' },
  { key: 'built_cart',      label: 'Built a cart',    className: 'text-violet-500' },
  { key: 'reached_info',    label: 'Reached info',    className: 'text-amber-500' },
  { key: 'reached_payment', label: 'Reached payment', className: 'text-orange-500' },
  { key: 'purchased',       label: 'Purchased',       className: 'text-emerald-500' },
];

const ANALYTICS_VIEWS = [
  { key: 'funnel', label: 'Funnel' },
  { key: 'by_event', label: 'By event' },
  { key: 'audience', label: 'Audience' },
  { key: 'marketing', label: 'Marketing' },
];

const LEADS_VIEWS = [
  { key: 'captures', label: 'Email captures' },
  { key: 'abandoned', label: 'Abandoned carts' },
  { key: 'codes', label: 'Failed codes' },
];

const DINING_VIEWS = [
  { key: 'funnel', label: 'Funnel' },
  { key: 'by_event', label: 'By event' },
  { key: 'by_venue', label: 'By venue' },
  { key: 'raw', label: 'Raw picks' },
];

const OPERATIONS_VIEWS = [
  { key: 'live', label: 'Live' },
  { key: 'health', label: 'Health' },
  { key: 'maintenance', label: 'Maintenance' },
];

// Pill-style inner sub-tab row, shared by the Analytics/Leads/Dining tabs.
function SubTabs({ views, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1 text-xs mb-4">
      {views.map((v) => (
        <button
          key={v.key}
          onClick={() => onChange(v.key)}
          className={`px-3 py-1 rounded-md border ${value === v.key ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}
        >{v.label}</button>
      ))}
    </div>
  );
}

function dollars(c) { return c == null ? '—' : `$${(c/100).toFixed(2)}`; }
// The widget service stores SQLite CURRENT_TIMESTAMP strings — naive UTC
// ("2026-07-08 00:24:46"). Left as-is, new Date() parses them as BROWSER-local,
// shifting every stamp by the viewer's offset before the venue-TZ conversion.
// Tag them UTC; strings with an explicit offset/Z (e.g. event_start_time)
// pass through untouched.
function shortTime(iso, tz) {
  if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)) {
    iso = iso.replace(' ', 'T') + 'Z';
  }
  return formatStamp(iso, tz);
}

// Round 84.7: client-side CSV export of any row array (leads / recovery tables).
function downloadCsv(rows, filename) {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CsvButton({ rows, filename }) {
  return (
    <button onClick={() => downloadCsv(rows, filename)} disabled={!rows?.length}
      className="px-2 py-1 text-xs border border-border rounded hover:bg-accent disabled:opacity-40">
      Download CSV
    </button>
  );
}

const SUBTABS = [
  { key: 'analytics', label: 'Analytics' },
  { key: 'leads', label: 'Leads' },
  { key: 'dining', label: 'Dining upsell' },
  { key: 'queue', label: 'Queue' },
  { key: 'evidence', label: 'Device evidence' },
  { key: 'operations', label: 'Operations' },
  { key: 'config', label: 'Widget config' },
];

export default function UniverseWidget() {
  const { tenantSlug } = useParams();
  const [tab, setTab] = useState('analytics');
  const [evidenceAllowed, setEvidenceAllowed] = useState(false);

  useEffect(() => {
    let active = true;
    apiFetch('/api/me/tenants')
      .then((r) => r.json())
      .then((data) => {
        if (!active) return;
        const tenant = (data.tenants || []).find((item) => item.slug === tenantSlug);
        setEvidenceAllowed(
          data.role === 'global_admin'
          || ['admin', 'ops'].includes(tenant?.role),
        );
      })
      .catch(() => { if (active) setEvidenceAllowed(false); });
    return () => { active = false; };
  }, [tenantSlug]);

  const visibleTabs = SUBTABS.filter((item) => item.key !== 'evidence' || evidenceAllowed);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Ticketing Widget</h1>
      </div>
      <div className="border-b border-border mb-4">
        <div className="flex gap-1 overflow-x-auto overflow-y-hidden">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-sm border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${tab === t.key ? 'border-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >{t.label}</button>
          ))}
        </div>
      </div>
      {tab === 'analytics' && <Analytics tenantSlug={tenantSlug} />}
      {tab === 'leads' && <Leads tenantSlug={tenantSlug} />}
      {tab === 'dining' && <Dining tenantSlug={tenantSlug} />}
      {tab === 'queue' && <Queue tenantSlug={tenantSlug} />}
      {tab === 'evidence' && evidenceAllowed && <DeviceEvidence tenantSlug={tenantSlug} />}
      {tab === 'operations' && <Operations tenantSlug={tenantSlug} evidenceAllowed={evidenceAllowed} />}
      {tab === 'config' && <WidgetConfig tenantSlug={tenantSlug} />}
    </div>
  );
}

function Analytics({ tenantSlug }) {
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [byEvent, setByEvent] = useState(null);
  const [declines, setDeclines] = useState(null);
  const [meta, setMeta] = useState(null);
  const [threeDs, setThreeDs] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [view, setView] = useState('funnel'); // inner sub-tab
  const [hideZero, setHideZero] = useState(true);
  // By-event table defaults to chronological so it can anchor on upcoming.
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const [trend, setTrend] = useState(null);
  const [expandedListing, setExpandedListing] = useState(null);
  // Per-listing daily series cache, keyed `${listing_id}:${days}` so a days
  // change naturally misses and refetches.
  const [listingTrends, setListingTrends] = useState({});
  // Round 84.7: multi-touch attribution + raw traffic.
  const [attrModel, setAttrModel] = useState('last_touch');
  const [attr, setAttr] = useState(null);
  const [traffic, setTraffic] = useState(null);
  // By-event chronological scroll: park the viewport on the first upcoming event.
  const scrollRef = useRef(null);
  const firstUpcomingRef = useRef(null);
  const todayStr = new Date().toISOString().slice(0, 10);
  // Audience map — lazy per-tab like Marketing. All funnel stages arrive in
  // one response; the stage chips switch metric locally. Stage state lives
  // HERE (not in AudienceMap): a days change nulls funnel → the tab renders
  // the Loading early-return → AudienceMap unmounts and would forget its
  // selected stage on remount.
  const [geo, setGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoStage, setGeoStage] = useState('opens');
  useEffect(() => {
    if (view !== 'audience') return;
    setGeoLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/geo-summary?days=${days}`)
      .then((r) => r.json())
      .then((d) => { if (!d.error && d.days === days) setGeo(d); })
      .catch(() => {})
      .finally(() => setGeoLoading(false));
  }, [tenantSlug, days, view]);

  // Attribution + traffic render only on the Marketing sub-tab — fetch them
  // only while it's active so range toggles on Funnel/By-event don't pay for
  // (or wait behind) data they never show.
  useEffect(() => {
    if (view !== 'marketing') return;
    setAttr(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/attribution?days=${days}&model=${attrModel}`)
      .then((r) => r.json()).then((d) => !d.error && setAttr(d)).catch(() => {});
  }, [tenantSlug, days, attrModel, view]);
  useEffect(() => {
    if (view !== 'marketing') return;
    setTraffic(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/page-views?days=${days}`)
      .then((r) => r.json()).then((d) => !d.error && setTraffic(d)).catch(() => {});
  }, [tenantSlug, days, view]);

  // Meta summary fetched separately — failure (no creds) should NOT
  // block the rest of the analytics page.
  useEffect(() => {
    setMeta(null);
    apiFetch(`/api/tenants/${tenantSlug}/meta-account-summary?days=${days}`)
      .then((r) => r.json().then((b) => ({ ok: r.ok, body: b })))
      .then(({ ok, body }) => { if (ok && body.summary) setMeta(body); })
      .catch(() => {});
  }, [tenantSlug, days]);

  useEffect(() => {
    setFunnel(null); setByEvent(null); setError(null); setThreeDs(null);
    setTrend(null); setExpandedListing(null);
    // 3DS stats — independent fetch, soft-fail if not available
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/3ds-stats?days=${days}`)
      .then((r) => r.json()).then((d) => { if (!d.error) setThreeDs(d); }).catch(() => {});
    // Daily trend series — independent fetch, soft-fail so a chart problem
    // never blanks the rest of the analytics page. 24h uses venue-local hour
    // buckets (granularity=hour) so the chart isn't a single day point.
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/funnel-daily?days=${days}${days === 1 ? '&granularity=hour' : ''}`)
      .then((r) => r.json())
      // Guard against a slow response for a previously-selected range landing
      // after the user has switched days — only accept matching-window data.
      .then((d) => { if (!d.error && d.days === days) setTrend(d); }).catch(() => {});
    Promise.all([
      apiFetch(`/api/tenants/${tenantSlug}/universe-widget/funnel?days=${days}`).then((r) => r.json()),
      apiFetch(`/api/tenants/${tenantSlug}/universe-widget/funnel-by-event?days=${days}`).then((r) => r.json()),
      apiFetch(`/api/tenants/${tenantSlug}/universe-widget/declines?days=${days}`).then((r) => r.json()),
    ]).then(([f, e, d]) => {
      if (f.error || e.error || d.error) throw new Error(f.error || e.error || d.error);
      setFunnel(f); setByEvent(e); setDeclines(d);
    }).catch((err) => setError(err.message));
  }, [tenantSlug, days]);

  const sortedListings = useMemo(() => {
    if (!byEvent?.listings) return [];
    const rows = hideZero ? byEvent.listings.filter((l) => l.opens > 0) : byEvent.listings;
    const conv = (l) => (l.opens > 0 ? l.purchased / l.opens : 0);
    const cmp = (a, b) => {
      let av, bv;
      // Null-date rows ("no matching event") can't sit on the timeline — sink
      // them to the bottom (high sentinel) regardless of sort direction.
      if (sortKey === 'date')  { av = a.event_date ? a.event_date.slice(0, 10) : '9999'; bv = b.event_date ? b.event_date.slice(0, 10) : '9999'; }
      else if (sortKey === 'event') { av = a.event_talent || a.listing_title || ''; bv = b.event_talent || b.listing_title || ''; }
      else if (sortKey === 'conv')  { av = conv(a); bv = conv(b); }
      else                          { av = a[sortKey] || 0; bv = b[sortKey] || 0; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };
    return [...rows].sort(cmp);
  }, [byEvent, hideZero, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'event' || key === 'date' ? 'asc' : 'desc'); }
  };
  const sortIndicator = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Chronological mode = the default; only then do we anchor the scroll and
  // draw the today divider. Re-sorting by any column opts out of both.
  const chronological = sortKey === 'date' && sortDir === 'asc';
  const firstUpcomingIdx = useMemo(() => (
    chronological
      ? sortedListings.findIndex((l) => l.event_date && l.event_date.slice(0, 10) >= todayStr)
      : -1
  ), [sortedListings, chronological, todayStr]);

  // Park the scroll container on the first current/upcoming event: past rows
  // above (scroll up), future below (scroll down). No upcoming → show bottom.
  useLayoutEffect(() => {
    if (view !== 'by_event' || !chronological) return;
    const c = scrollRef.current;
    if (!c) return;
    const r = firstUpcomingRef.current;
    if (!r) { c.scrollTop = c.scrollHeight; return; }
    const head = c.querySelector('thead');
    c.scrollTop += r.getBoundingClientRect().top - c.getBoundingClientRect().top - (head?.offsetHeight || 0);
  }, [view, chronological, sortedListings, firstUpcomingIdx]);

  const toggleExpand = (listingId) => {
    if (expandedListing === listingId) { setExpandedListing(null); return; }
    setExpandedListing(listingId);
    const cacheKey = `${listingId}:${days}`;
    if (listingTrends[cacheKey]) return;
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/funnel-daily?days=${days}&listing_id=${encodeURIComponent(listingId)}${days === 1 ? '&granularity=hour' : ''}`)
      .then((r) => r.json())
      .then((d) => { if (!d.error) setListingTrends((prev) => ({ ...prev, [cacheKey]: d.series })); })
      .catch(() => {});
  };

  if (error) return <div className="text-destructive">{error}</div>;
  if (!funnel) return <div className="text-muted-foreground">Loading…</div>;

  const f = funnel.funnel;
  const stages = [
    // Page views only started accruing when host-page beaconing shipped —
    // hide the stage entirely until there's data in the window.
    ...(f.page_views > 0 ? [{ key: 'page_views', label: 'Page views', value: f.page_views }] : []),
    { key: 'opens',           label: 'Opens',           value: f.opens },
    { key: 'built_cart',      label: 'Built a cart',    value: f.built_cart },
    { key: 'reached_info',    label: 'Reached info',    value: f.reached_info },
    { key: 'reached_payment', label: 'Reached payment', value: f.reached_payment },
    { key: 'purchased',       label: 'Purchased',       value: f.purchased },
  ];
  // Anchor bars + "% of" copy to the LARGEST stage, not stages[0]. In steady
  // state that's page views (descending funnel), but page-view tracking only
  // started accruing June 2026 — while the window still spans pre-tracking
  // days, opens > views and anchoring to views would produce 10000%+ noise.
  const topStage = stages.reduce((a, b) => ((b.value || 0) > (a.value || 0) ? b : a));
  const top = topStage.value || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Analytics ({days === 1 ? 'last 24 hours' : `last ${days} days`})
        </h2>
        <div className="flex flex-wrap gap-1 text-xs">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              title={d === 1 ? 'Last 24 hours' : `Last ${d} days (rolling)`}
              onClick={() => setDays(d)}
              className={`px-2 py-1 rounded-md border ${days === d ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}
            >{d === 1 ? '24h' : `${d}d`}</button>
          ))}
        </div>
      </div>

      {/* Inner sub-tabs, pill style, distinct from the underline top-level tabs */}
      <div className="flex flex-wrap gap-1 text-xs mb-4">
        {ANALYTICS_VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`px-3 py-1 rounded-md border ${view === v.key ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}
          >{v.label}</button>
        ))}
      </div>

      {view === 'funnel' && (<>
      <div className="border border-border rounded-md overflow-hidden mb-6 bg-card">
        {stages.map((s, i) => {
          const prev = i > 0 ? stages[i - 1].value : null;
          const pctOfTop = top > 0 ? (s.value / top) * 100 : 0;
          // Suppress >100% ratios — a funnel descends, so anything above 100
          // is the page-views rollout transient (views < opens), not signal.
          const pctOfPrev = prev != null && prev > 0 && s.value <= prev
            ? (s.value / prev) * 100 : null;
          const showPctOfTop = s.key !== topStage.key && pctOfTop <= 100;
          return (
            <div key={s.key} className={`px-4 py-3 ${i > 0 ? 'border-t border-border' : ''} flex items-center gap-4`}>
              <div className="flex-1">
                <div className="text-sm font-medium">{s.label}</div>
                {(showPctOfTop || pctOfPrev != null) && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {showPctOfTop && <>{pctOfTop.toFixed(1)}% of {topStage.label.toLowerCase()}</>}
                    {pctOfPrev != null && stages[i - 1].key !== topStage.key && (
                      <>{showPctOfTop ? ' · ' : ''}{pctOfPrev.toFixed(1)}% of {stages[i - 1].label.toLowerCase()}</>
                    )}
                  </div>
                )}
              </div>
              <div className="w-32 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${Math.min(100, pctOfTop)}%` }}
                />
              </div>
              <div className="text-2xl font-semibold tabular-nums w-24 text-right">
                {(s.value || 0).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {trend?.series?.length > 0 && (
        <div className="mb-6 border border-border rounded-md p-3 bg-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{days === 1 ? 'Hourly trend (24h)' : `Daily trend (${days}d)`}</div>
            <div className="text-[10px] text-muted-foreground">{days === 1 ? 'Hours are in venue time (ET); the last hour is partial.' : 'Days are in venue time (ET); today is partial.'}</div>
          </div>
          <TrendChart series={trend.series} stages={TREND_STAGES} />
        </div>
      )}
      </>)}

      {view === 'marketing' && meta?.summary && (
        <div className="mb-6 border border-border rounded-md p-3 bg-card">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Meta ad spend ({days}d)</div>
              <div className="text-lg font-semibold tabular-nums">${(meta.summary.spend_cents / 100).toFixed(2)}</div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Meta purchases</div>
              <div className="text-lg font-semibold tabular-nums">{meta.summary.purchases.toLocaleString()}</div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Meta ROAS</div>
              <div className="text-lg font-semibold tabular-nums">{meta.summary.spend_cents > 0 ? `${meta.summary.roas.toFixed(2)}x` : '—'}</div>
            </div>
          </div>
          {meta.cache_meta && (
            <div className="mt-2 pt-2 border-t border-border flex justify-end">
              <FreshnessBadge cacheMeta={meta.cache_meta} />
            </div>
          )}
        </div>
      )}

      {view === 'funnel' && threeDs && threeDs.total_attempts > 0 && (
        <div className="mb-6 border border-border rounded-md p-3 bg-card">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Payment attempts ({days}d)</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total:</span>{' '}
              <span className="font-semibold tabular-nums">{threeDs.total_attempts.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Succeeded:</span>{' '}
              <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{threeDs.succeeded.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground ml-1">({((threeDs.succeeded / threeDs.total_attempts) * 100).toFixed(1)}%)</span>
            </div>
            {threeDs.three_ds_abandoned > 0 && (
              <div>
                <span className="text-muted-foreground">3DS abandoned:</span>{' '}
                <span className="font-semibold tabular-nums text-amber-600 dark:text-amber-400">{threeDs.three_ds_abandoned.toLocaleString()}</span>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">CVC fail:</span>{' '}
              <span className="font-semibold tabular-nums">{threeDs.cvc_fail.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Insufficient funds:</span>{' '}
              <span className="font-semibold tabular-nums">{threeDs.insufficient_funds.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {view === 'by_event' && byEvent && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              By event ({sortedListings.length}{hideZero && byEvent.count > sortedListings.length ? ` of ${byEvent.count}` : ''})
            </h2>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
                className="rounded"
              />
              Hide 0 opens
            </label>
          </div>
          {chronological && (
            <div className="text-[10px] text-muted-foreground mb-1.5">
              Scrolled to today. Scroll up for past events, down for upcoming. Click a column to re-sort.
            </div>
          )}
          <div ref={scrollRef} className="border border-border rounded-md overflow-auto max-h-[65vh] relative">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left sticky top-0 z-10">
                <tr>
                  <th onClick={() => onSort('date')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted">Date{sortIndicator('date')}</th>
                  <th onClick={() => onSort('event')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted">Event{sortIndicator('event')}</th>
                  <th onClick={() => onSort('page_views')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Views{sortIndicator('page_views')}</th>
                  <th onClick={() => onSort('opens')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Opens{sortIndicator('opens')}</th>
                  <th onClick={() => onSort('built_cart')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Cart{sortIndicator('built_cart')}</th>
                  <th onClick={() => onSort('reached_info')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Info{sortIndicator('reached_info')}</th>
                  <th onClick={() => onSort('reached_payment')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Pay{sortIndicator('reached_payment')}</th>
                  <th onClick={() => onSort('purchased')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Purchased{sortIndicator('purchased')}</th>
                  <th onClick={() => onSort('conv')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted text-right">Conv %{sortIndicator('conv')}</th>
                  <th className="px-2 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {sortedListings.map((l, idx) => {
                  const conv = l.opens > 0 ? (l.purchased / l.opens) * 100 : 0;
                  const isClickable = !!l.event_id;
                  const isExpanded = expandedListing === l.listing_id;
                  const cachedSeries = listingTrends[`${l.listing_id}:${days}`];
                  const isFirstUpcoming = idx === firstUpcomingIdx;
                  return (
                    <Fragment key={l.listing_id}>
                    <tr
                      ref={isFirstUpcoming ? firstUpcomingRef : null}
                      onClick={() => isClickable && navigate(`/${tenantSlug}/event/${l.event_id}`)}
                      className={`${isFirstUpcoming ? 'border-t-2 border-primary/50' : 'border-t border-border'} ${isClickable ? 'hover:bg-accent/30 cursor-pointer' : ''}`}
                    >
                      <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        {isFirstUpcoming && (
                          <span className="mr-1.5 text-[9px] uppercase tracking-wide font-medium text-primary bg-primary/10 rounded px-1 py-0.5 align-middle">Upcoming</span>
                        )}
                        {l.event_date ? new Date(l.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="truncate inline-block max-w-xs">
                          {l.event_talent || l.listing_title || l.listing_id}
                        </span>
                        {!l.event_id && (
                          <span className="ml-1 text-[10px] text-muted-foreground">(no matching event)</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{(l.page_views || 0).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{(l.opens || 0).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{(l.built_cart || 0).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{(l.reached_info || 0).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{(l.reached_payment || 0).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{(l.purchased || 0).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{conv.toFixed(1)}%</td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(l.listing_id); }}
                          title={days === 1 ? 'Hourly trend' : 'Daily trend'}
                          className={`p-1 rounded hover:bg-accent ${isExpanded ? 'text-primary' : 'text-muted-foreground'}`}
                        ><TrendingUp size={14} /></button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-t border-border bg-muted/20">
                        <td colSpan={10} className="px-4 py-3">
                          {cachedSeries
                            ? <TrendChart series={cachedSeries} stages={TREND_STAGES} height={140} />
                            : <div className="text-xs text-muted-foreground">Loading…</div>}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {sortedListings.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">No listings with widget traffic in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'funnel' && declines && (
        <>
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Failed payment attempts ({declines.count} total, last {days} days)
          </h2>
          <div className="border border-border rounded-md p-3 mb-2">
            {declines.count === 0 ? (
              <div className="text-sm text-muted-foreground">None.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(declines.by_code).map(([code, n]) => (
                  <span key={code} className="text-xs px-2 py-1 bg-destructive/10 text-destructive rounded">
                    {code}: {n}
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {view === 'audience' && (
        <AudienceMap data={geo} loading={geoLoading} stage={geoStage} onStageChange={setGeoStage} />
      )}

      {view === 'marketing' && (<>
      {/* Round 84.7: multi-touch attribution */}
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Ref attribution</h2>
        <select value={attrModel} onChange={(e) => setAttrModel(e.target.value)}
          className="px-2 py-1 border border-border rounded bg-background text-xs">
          <option value="last_touch">Last touch</option>
          <option value="first_touch">First touch</option>
          <option value="linear">Linear</option>
          <option value="time_decay">Time decay</option>
          <option value="position_based">Position based</option>
        </select>
        <span className="text-xs text-muted-foreground">multi-touch journeys, internal view (Universe pays last-touch)</span>
      </div>
      {attr && attr.totals && Object.keys(attr.totals).length ? (
        <>
        {/* Mobile: ref cards (the table below is md+ only) */}
        <div className="md:hidden space-y-2 mb-6">
          {Object.entries(attr.totals).sort((a, b) => (b[1].revenue_cents || 0) - (a[1].revenue_cents || 0)).map(([ref, t]) => (
            <div key={ref} className="border border-border rounded-md p-3 bg-card text-sm">
              <div className="font-mono font-medium mb-1">{ref}</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>Credit: <span className="tabular-nums text-foreground">{(t.credit ?? 0).toFixed(2)}</span></span>
                <span>Attributed revenue: <span className="tabular-nums text-foreground">{dollars(t.revenue_cents)}</span></span>
              </div>
            </div>
          ))}
        </div>
        <div className="border border-border rounded-md overflow-x-auto mb-6 hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-muted-foreground">
              <tr><th className="px-3 py-1.5">Ref</th><th className="px-3 py-1.5 text-right">Credit</th><th className="px-3 py-1.5 text-right">Attributed revenue</th></tr>
            </thead>
            <tbody>
              {Object.entries(attr.totals).sort((a, b) => (b[1].revenue_cents || 0) - (a[1].revenue_cents || 0)).map(([ref, t]) => (
                <tr key={ref} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono">{ref}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{(t.credit ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{dollars(t.revenue_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : <div className="text-sm text-muted-foreground mb-6">No attributed journeys in this window.</div>}

      {/* Round 84.7: traffic by slug */}
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
        Traffic{traffic ? `, ${traffic.total} views` : ''}
      </h2>
      {traffic && traffic.by_slug?.length ? (
        <>
        {/* Mobile: slug cards (the table below is md+ only) */}
        <div className="md:hidden space-y-2">
          {traffic.by_slug.slice(0, 50).map((s) => (
            <div key={s.slug} className="border border-border rounded-md p-3 bg-card text-sm">
              <div className="font-mono text-xs font-medium mb-1">{s.slug || '—'}</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>Views: <span className="tabular-nums text-foreground">{s.views}</span></span>
                <span>Sessions: <span className="tabular-nums text-foreground">{s.sessions}</span></span>
              </div>
            </div>
          ))}
        </div>
        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-muted-foreground">
              <tr><th className="px-3 py-1.5">Slug</th><th className="px-3 py-1.5 text-right">Views</th><th className="px-3 py-1.5 text-right">Sessions</th></tr>
            </thead>
            <tbody>
              {traffic.by_slug.slice(0, 50).map((s) => (
                <tr key={s.slug} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono text-xs">{s.slug || '—'}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{s.views}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{s.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : <div className="text-sm text-muted-foreground">No page-view data in this window.</div>}
      </>)}
    </div>
  );
}

function Dining({ tenantSlug }) {
  const navigate = useNavigate();
  const tz = useVenueTz();
  const [view, setView] = useState('funnel');
  const [dining, setDining] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [intents, setIntents] = useState(null); // Round 84.7: raw pre-info slot picks
  const [eventSort, setEventSort] = useState({ key: 'estimated_value_cents', direction: 'desc' });

  useEffect(() => {
    setDining(null); setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/dining-funnel?days=${days}`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setDining(d); })
      .catch((e) => setError(e.message));
  }, [tenantSlug, days]);

  useEffect(() => {
    setIntents(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/dining-intents?days=${days}`)
      .then((r) => r.json()).then((d) => !d.error && setIntents(d)).catch(() => {});
  }, [tenantSlug, days]);

  if (error) return <div className="text-destructive">{error}</div>;
  if (!dining) return <div className="text-muted-foreground">Loading…</div>;

  const rollup = dining.rollup;
  const hasBookingSplit = rollup.with_ticket_booked != null && rollup.standalone_booked != null;
  const stages = [
    { label: 'Opens',                 value: rollup.opens,           pctOf: null },
    { label: 'Saw dining card',       value: rollup.saw_dining_card, pctOf: rollup.opens,           pctOfLabel: 'opens' },
    { label: 'Picked a slot',         value: rollup.picked_slot,     pctOf: rollup.saw_dining_card, pctOfLabel: 'card-views', covers: rollup.covers },
    { label: 'Confirmed reservation', value: rollup.dinners_booked,  pctOf: rollup.picked_slot,     pctOfLabel: 'slot picks', covers: rollup.booked_covers },
  ];
  const overallConv = rollup.opens > 0 ? (rollup.dinners_booked / rollup.opens) * 100 : 0;
  const previousPeriod = dining.previous_period || null;
  const estimatedValue = rollup.estimated_value_cents || 0;
  const previousEstimatedValue = previousPeriod?.estimated_value_cents || 0;
  const estimatedValueDelta = previousEstimatedValue > 0
    ? ((estimatedValue - previousEstimatedValue) / previousEstimatedValue) * 100
    : null;
  const comparisonWindow = days === 1 ? 'previous 24h' : `previous ${days}d`;
  const estimatedValueFor = (row, prefix = '') => {
    const value = row[`${prefix}estimated_value_cents`];
    const pricedCovers = row[`${prefix}estimated_value_priced_covers`] || 0;
    const unpricedCovers = row[`${prefix}estimated_value_unpriced_covers`] || 0;
    if (value == null || (unpricedCovers > 0 && pricedCovers === 0)) return '—';
    return `${formatUsdCents(value)}${unpricedCovers > 0 ? '*' : ''}`;
  };
  const venueEstimatedValue = (venue) => estimatedValueFor(venue);
  const comparisonValue = (current, previous) => {
    if (previous > 0) return ((current - previous) / previous) * 100;
    if (current > 0) return Number.POSITIVE_INFINITY;
    return 0;
  };
  const renderMetricDelta = (current, previous, showPrior = false, priorFormatter = (value) => value.toLocaleString()) => {
    const delta = comparisonValue(current || 0, previous || 0);
    const className = delta > 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : delta < 0 ? 'text-destructive' : 'text-muted-foreground';
    return (
      <div className={`text-[10px] whitespace-nowrap ${className}`} title={`Prior: ${priorFormatter(previous || 0)}`}>
        {Number.isFinite(delta)
          ? delta === 0 ? '—' : `${delta > 0 ? '▲' : '▼'} ${Math.abs(delta).toFixed(1)}%`
          : 'New'}
        {showPrior && <div className="text-muted-foreground">Prior: {priorFormatter(previous || 0)}</div>}
      </div>
    );
  };
  const eventSortValue = (row, key) => {
    if (key === 'event') return String(row.event_talent || row.listing_title || row.listing_id || 'Unattributed').toLowerCase();
    if (key === 'date') return Date.parse(row.event_date || row.event_start_time || '') || 0;
    if (key === 'pick_to_book') return row.picks > 0 ? row.booked / row.picks : 0;
    if (key === 'value_delta') return comparisonValue(row.estimated_value_cents || 0, row.previous_estimated_value_cents || 0);
    return row[key] || 0;
  };
  const diningEvents = [...(dining.by_event || [])].sort((a, b) => {
    const av = eventSortValue(a, eventSort.key);
    const bv = eventSortValue(b, eventSort.key);
    const result = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
    return eventSort.direction === 'asc' ? result : -result;
  });
  const onEventSort = (key) => {
    setEventSort((current) => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'event' || key === 'date' ? 'asc' : 'desc' });
  };
  const eventSortIndicator = (key) => eventSort.key === key
    ? (eventSort.direction === 'asc' ? ' ↑' : ' ↓')
    : '';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Dining upsell ({days === 1 ? 'last 24 hours' : `last ${days} days`})
        </h2>
        <div className="flex flex-wrap gap-1 text-xs">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              title={d === 1 ? 'Last 24 hours' : `Last ${d} days (rolling)`}
              onClick={() => setDays(d)}
              className={`px-2 py-1 rounded-md border ${days === d ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}
            >{d === 1 ? '24h' : `${d}d`}</button>
          ))}
        </div>
      </div>

      <SubTabs views={DINING_VIEWS} value={view} onChange={setView} />

      {view === 'funnel' && (<>
      <div className="border border-border rounded-md bg-card overflow-hidden mb-6">
        {stages.map((row, i) => {
          const pct = row.pctOf > 0 ? (row.value / row.pctOf) * 100 : null;
          return (
            <div key={row.label} className={`px-4 py-3 ${i > 0 ? 'border-t border-border' : ''} flex items-center gap-4`}>
              <div className="flex-1">
                <div className="text-sm font-medium">{row.label}</div>
                {pct != null && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {pct.toFixed(1)}% of {row.pctOfLabel}
                  </div>
                )}
              </div>
              <div className="w-32 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${rollup.opens > 0 ? Math.min(100, (row.value / rollup.opens) * 100) : 0}%` }}
                />
              </div>
              <div className="w-24 text-right">
                <div className="text-2xl font-semibold tabular-nums">
                  {(row.value || 0).toLocaleString()}
                </div>
                {row.covers != null && (
                  <div className="text-[11px] text-muted-foreground tabular-nums -mt-0.5">
                    {(row.covers || 0).toLocaleString()} covers
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rollup.estimated_value_cents != null && (
        <div className="border border-border rounded-md bg-card px-4 py-3 mb-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Estimated dining value</div>
              <div className="text-2xl font-semibold tabular-nums">{formatUsdCents(estimatedValue)}</div>
              <div className="text-[11px] text-muted-foreground">Confirmed covers × frozen average spend per cover</div>
            </div>
            {previousPeriod && (
              <div className="text-right text-xs tabular-nums">
                {estimatedValueDelta != null ? (
                  <div className={estimatedValueDelta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}>
                    {estimatedValueDelta >= 0 ? '▲' : '▼'} {Math.abs(estimatedValueDelta).toFixed(1)}% vs {comparisonWindow}
                  </div>
                ) : estimatedValue > 0 ? (
                  <div className="text-emerald-600 dark:text-emerald-400">New vs {comparisonWindow}</div>
                ) : (
                  <div className="text-muted-foreground">No value in either period</div>
                )}
                <div className="text-muted-foreground">Prior: {formatUsdCents(previousEstimatedValue)}</div>
              </div>
            )}
          </div>
          {(rollup.estimated_value_unpriced_covers || 0) > 0 && (
            <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">
              {(rollup.estimated_value_unpriced_covers || 0).toLocaleString()} confirmed covers excluded because no frozen rate is available.
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-muted-foreground mb-2">
        Overall conversion: <span className="font-medium text-foreground">{overallConv.toFixed(2)}%</span> of widget opens resulted in a confirmed dinner reservation.
      </div>

      <div className="text-xs text-muted-foreground mb-2">
        Covers selected: <span className="font-medium text-foreground">{(rollup.covers || 0).toLocaleString()}</span>
        {rollup.picked_slot > 0 && (
          <>, avg party <span className="font-medium text-foreground">{(rollup.covers / rollup.picked_slot).toFixed(1)}</span></>
        )} (total diners across all slot picks, intent, not confirmed).
      </div>

      {hasBookingSplit && (
        <div className="text-xs text-muted-foreground mb-4">
          Booking split: <span className="font-medium text-foreground">{(rollup.with_ticket_booked || 0).toLocaleString()}</span>
          {' '}with tickets ({(rollup.with_ticket_booked_covers || 0).toLocaleString()} covers),{' '}
          <span className="font-medium text-foreground">{(rollup.standalone_booked || 0).toLocaleString()}</span>
          {' '}dinner-only ({(rollup.standalone_booked_covers || 0).toLocaleString()} covers).
        </div>
      )}
      </>)}

      {view === 'by_event' && (<>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          By event ({diningEvents.length})
        </h2>
        <div className="text-[10px] text-muted-foreground">Compared with {comparisonWindow}</div>
      </div>
      {diningEvents.length > 0 ? (<>
        <div className="text-[11px] text-muted-foreground mb-2">
          Confirmed reservations are attributed to the event frozen on the booking. Unattributed reservations remain visible so totals reconcile.
        </div>
        <div className="md:hidden space-y-2">
          {diningEvents.map((event) => {
            const isClickable = !!event.dashboard_event_id;
            const eventName = event.listing_id
              ? (event.event_talent || event.listing_title || event.listing_id)
              : 'Unattributed';
            const eventDate = event.event_date || event.event_start_time;
            const conversion = event.picks > 0 ? (event.booked / event.picks) * 100 : 0;
            return (
              <div
                key={JSON.stringify([event.listing_id, event.event_id])}
                onClick={() => isClickable && navigate(`/${tenantSlug}/event/${event.dashboard_event_id}`)}
                className={`border border-border rounded-md p-3 bg-card text-sm ${isClickable ? 'cursor-pointer hover:bg-accent/30' : ''}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="font-medium">{eventName}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {eventDate ? new Date(eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>Picks: <span className="tabular-nums text-foreground">{(event.picks || 0).toLocaleString()}</span></span>
                  <span>Selected covers: <span className="tabular-nums text-foreground">{(event.covers || 0).toLocaleString()}</span></span>
                  <span>Confirmed: <span className="tabular-nums font-medium text-foreground">{(event.booked || 0).toLocaleString()}</span></span>
                  <span>Booked covers: <span className="tabular-nums font-medium text-foreground">{(event.booked_covers || 0).toLocaleString()}</span></span>
                  <span>Est. value: <span className="tabular-nums font-medium text-foreground">{estimatedValueFor(event)}</span></span>
                  <span>Pick to book: <span className="tabular-nums text-foreground">{conversion.toFixed(1)}%</span></span>
                </div>
                <div className="flex gap-4 mt-1.5">
                  <div className="text-[10px] text-muted-foreground">Picks {renderMetricDelta(event.picks, event.previous_picks)}</div>
                  <div className="text-[10px] text-muted-foreground">Bookings {renderMetricDelta(event.booked, event.previous_booked)}</div>
                  <div className="text-[10px] text-muted-foreground">Value {renderMetricDelta(event.estimated_value_cents, event.previous_estimated_value_cents)}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th onClick={() => onEventSort('event')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted">Event{eventSortIndicator('event')}</th>
                <th onClick={() => onEventSort('date')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted">Date{eventSortIndicator('date')}</th>
                <th onClick={() => onEventSort('picks')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Picks{eventSortIndicator('picks')}</th>
                <th onClick={() => onEventSort('covers')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Selected covers{eventSortIndicator('covers')}</th>
                <th onClick={() => onEventSort('booked')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Confirmed{eventSortIndicator('booked')}</th>
                <th onClick={() => onEventSort('booked_covers')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Booked covers{eventSortIndicator('booked_covers')}</th>
                <th onClick={() => onEventSort('estimated_value_cents')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Est. value{eventSortIndicator('estimated_value_cents')}</th>
                <th onClick={() => onEventSort('pick_to_book')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Pick to book{eventSortIndicator('pick_to_book')}</th>
                <th onClick={() => onEventSort('value_delta')} className="px-3 py-2 font-medium text-right cursor-pointer hover:bg-muted">Value vs prior{eventSortIndicator('value_delta')}</th>
              </tr>
            </thead>
            <tbody>
              {diningEvents.map((event) => {
                const isClickable = !!event.dashboard_event_id;
                const eventName = event.listing_id
                  ? (event.event_talent || event.listing_title || event.listing_id)
                  : 'Unattributed';
                const eventDate = event.event_date || event.event_start_time;
                const conversion = event.picks > 0 ? (event.booked / event.picks) * 100 : 0;
                return (
                  <tr
                    key={JSON.stringify([event.listing_id, event.event_id])}
                    onClick={() => isClickable && navigate(`/${tenantSlug}/event/${event.dashboard_event_id}`)}
                    className={`border-t border-border ${isClickable ? 'cursor-pointer hover:bg-accent/30' : ''}`}
                  >
                    <td className="px-3 py-1.5">
                      <span className="truncate inline-block max-w-xs">{eventName}</span>
                      {event.listing_id && !event.dashboard_event_id && (
                        <span className="ml-1 text-[10px] text-muted-foreground">(no matching event)</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                      {eventDate ? new Date(eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {(event.picks || 0).toLocaleString()}
                      {renderMetricDelta(event.picks, event.previous_picks)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{(event.covers || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                      {(event.booked || 0).toLocaleString()}
                      {renderMetricDelta(event.booked, event.previous_booked)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{(event.booked_covers || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{estimatedValueFor(event)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{conversion.toFixed(1)}%</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {renderMetricDelta(
                        event.estimated_value_cents,
                        event.previous_estimated_value_cents,
                        true,
                        formatUsdCents,
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(diningEvents.some((event) => event.estimated_value_unpriced_covers > 0
          || event.previous_estimated_value_unpriced_covers > 0)) && (
          <div className="text-[10px] text-muted-foreground mt-1">* Estimated value excludes confirmed covers without a frozen rate.</div>
        )}
      </>) : (
        <div className="text-sm text-muted-foreground border border-border rounded-md p-4">
          No dining activity recorded by event in this range.
        </div>
      )}
      </>)}

      {view === 'by_venue' && (<>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
        By venue
      </h2>
      {dining.by_venue && dining.by_venue.length > 0 ? (
        <>
        {/* Mobile: venue cards (the table below is md+ only) */}
        <div className="md:hidden space-y-2">
          {dining.by_venue.map((v) => {
            const commit = v.picks > 0 ? (v.reached_info / v.picks) * 100 : 0;
            const shareOfPicks = rollup.picked_slot > 0 ? (v.picks / rollup.picked_slot) * 100 : 0;
            return (
              <div key={v.venue_name} className="border border-border rounded-md p-3 bg-card text-sm">
                <div className="font-medium mb-1">{v.venue_name}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span>Picks: <span className="tabular-nums text-foreground">{v.picks}</span></span>
                  <span>Covers: <span className="tabular-nums text-foreground">{(v.covers || 0).toLocaleString()}</span></span>
                  <span>Reached info: <span className="tabular-nums text-foreground">{v.reached_info}</span></span>
                  <span>Booked: <span className="tabular-nums font-medium text-foreground">{(v.booked || 0).toLocaleString()}</span></span>
                  <span>Booked covers: <span className="tabular-nums font-medium text-foreground">{(v.booked_covers || 0).toLocaleString()}</span></span>
                  <span>Est. value: <span className="tabular-nums font-medium text-foreground">{venueEstimatedValue(v)}</span></span>
                  <span>Commit: <span className="tabular-nums">{commit.toFixed(0)}%</span></span>
                  <span>% of all picks: <span className="tabular-nums">{shareOfPicks.toFixed(0)}%</span></span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Venue</th>
                <th className="px-3 py-2 font-medium text-right">Picks</th>
                <th className="px-3 py-2 font-medium text-right">Covers</th>
                <th className="px-3 py-2 font-medium text-right">Reached info</th>
                <th className="px-3 py-2 font-medium text-right">Booked</th>
                <th className="px-3 py-2 font-medium text-right">Booked covers</th>
                <th className="px-3 py-2 font-medium text-right">Est. value</th>
                <th className="px-3 py-2 font-medium text-right">Commit %</th>
                <th className="px-3 py-2 font-medium text-right">% of all picks</th>
              </tr>
            </thead>
            <tbody>
              {dining.by_venue.map((v) => {
                const commit = v.picks > 0 ? (v.reached_info / v.picks) * 100 : 0;
                const shareOfPicks = rollup.picked_slot > 0 ? (v.picks / rollup.picked_slot) * 100 : 0;
                return (
                  <tr key={v.venue_name} className="border-t border-border">
                    <td className="px-3 py-1.5">{v.venue_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{v.picks}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{(v.covers || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{v.reached_info}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{(v.booked || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{(v.booked_covers || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{venueEstimatedValue(v)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{commit.toFixed(0)}%</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{shareOfPicks.toFixed(0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div className="text-sm text-muted-foreground border border-border rounded-md p-4">
          No slot picks recorded in this range.
        </div>
      )}
      </>)}

      {view === 'raw' && (<>
      {/* Round 84.7: raw pre-info slot picks */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Raw slot picks{intents ? ` (${intents.count})` : ''}
        </h2>
        <CsvButton rows={intents?.rows} filename={`${tenantSlug}-dining-intents.csv`} />
      </div>
      {intents && intents.rows.length ? (
        <>
        {/* Mobile: slot-pick cards (the table below is md+ only) */}
        <div className="md:hidden space-y-2">
          {intents.rows.slice(0, 60).map((r) => (
            <div key={r.id} className="border border-border rounded-md p-3 bg-card text-sm">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-medium">{r.venue_name || '—'}</span>
                <span className="font-mono text-xs text-muted-foreground">{shortTime(r.created_at, tz)}</span>
              </div>
              <div className="text-xs">{[r.slot_date, r.slot_time].filter(Boolean).join(' ') || '—'}</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                <span>Party: <span className="tabular-nums text-foreground">{r.party_size ?? '—'}</span></span>
                <span>Reached info: {r.linked_to_abandoned_cart ? '✓' : '—'}</span>
                <span>{r.listing_title || '—'}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5">When</th><th className="px-3 py-1.5">Venue</th>
                <th className="px-3 py-1.5">Slot</th><th className="px-3 py-1.5 text-right">Party</th>
                <th className="px-3 py-1.5">Reached info</th><th className="px-3 py-1.5">Listing</th>
              </tr>
            </thead>
            <tbody>
              {intents.rows.slice(0, 60).map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{shortTime(r.created_at, tz)}</td>
                  <td className="px-3 py-1.5 text-xs">{r.venue_name || '—'}</td>
                  <td className="px-3 py-1.5 text-xs">{[r.slot_date, r.slot_time].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.party_size ?? '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.linked_to_abandoned_cart ? '✓' : '—'}</td>
                  <td className="px-3 py-1.5 text-xs">{r.listing_title || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : <div className="text-sm text-muted-foreground">No raw slot picks in this range.</div>}
      </>)}
    </div>
  );
}

// ── Round 84.2: CF virtual waiting room ──────────────────────────────────
function Queue({ tenantSlug }) {
  const [data, setData] = useState(null);     // { worker, gates }
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);   // editable map slug → {enabled, admission_rate, ab_test}
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [newSlug, setNewSlug] = useState('');
  const [ab, setAb] = useState(null);

  // Live counters refresh every 10s WITHOUT clobbering the editable draft.
  const loadLive = () => {
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/queue`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setDraft((prev) => prev || Object.fromEntries(d.gates.map((g) => [g.slug, { ...g.config }])));
      })
      .catch((e) => setError(e.message));
  };
  useEffect(() => {
    loadLive();
    const t = setInterval(loadLive, 10000);
    return () => clearInterval(t);
  }, [tenantSlug]);

  useEffect(() => {
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/queue/ab-results?days=30`)
      .then((r) => r.json()).then((d) => !d.error && setAb(d)).catch(() => {});
  }, [tenantSlug]);

  const setGate = (slug, patch) =>
    setDraft((d) => ({ ...d, [slug]: { ...d[slug], ...patch } }));
  const removeGate = (slug) =>
    setDraft((d) => { const n = { ...d }; delete n[slug]; return n; });
  const addGate = () => {
    const s = newSlug.trim();
    if (!s || (draft && s in draft)) return;
    setDraft((d) => ({ ...(d || {}), [s]: { enabled: false, admission_rate: 200, ab_test: false, mode: 'surge' } }));
    setNewSlug('');
  };

  const save = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/queue`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft || {}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      const allPushed = Object.values(d.pushed || {}).every(Boolean);
      setSaveMsg(allPushed ? '✓ Saved' : '✓ Saved (some gates will sync on next widget restart)');
      loadLive();
    } catch (e) { setSaveMsg(`✗ ${e.message}`); }
    finally { setSaving(false); setTimeout(() => setSaveMsg(null), 5000); }
  };

  if (error) return <div className="text-destructive">{error}</div>;
  if (!data || !draft) return <div className="text-muted-foreground">Loading…</div>;

  const liveBySlug = Object.fromEntries((data.gates || []).map((g) => [g.slug, g.live]));
  const slugs = Object.keys(draft);

  return (
    <div className="space-y-6">
      {/* Worker health */}
      <div className="flex items-center gap-2 text-sm">
        <span className={`inline-block w-2 h-2 rounded-full ${data.worker.healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
        <span className="text-muted-foreground">Queue worker</span>
        <span className="font-medium">{data.worker.healthy ? 'Healthy' : 'Unreachable (gates fail open)'}</span>
        {data.worker.url && <span className="text-xs text-muted-foreground truncate">{data.worker.url}</span>}
      </div>

      {/* Gate cards */}
      <div className="space-y-3">
        {slugs.length === 0 && (
          <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">
            No queue gates. Add a listing slug below to gate it.
          </div>
        )}
        {slugs.map((slug) => {
          const g = draft[slug];
          const live = liveBySlug[slug];
          return (
            <div key={slug} className="border border-border rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <code className="text-sm font-medium">{slug}</code>
                <button onClick={() => removeGate(slug)} className="text-xs text-destructive hover:underline">Remove</button>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={g.enabled}
                    onChange={(e) => setGate(slug, { enabled: e.target.checked })} />
                  Enabled
                </label>
                <label className="flex items-center gap-2 text-sm">
                  Mode
                  <select value={g.mode || 'surge'}
                    onChange={(e) => setGate(slug, { mode: e.target.value })}
                    className="px-2 py-1 border border-border rounded bg-background text-sm">
                    <option value="surge">Surge (queue only above rate)</option>
                    <option value="always">Always (everyone queues)</option>
                    <option value="concurrency">Concurrency (max active at once)</option>
                  </select>
                </label>
                {g.mode === 'concurrency' ? (
                  <>
                    <label className="flex items-center gap-2 text-sm">
                      Max active
                      <input type="number" min="1" max="100000" value={g.max_active ?? 50}
                        onChange={(e) => setGate(slug, { max_active: parseInt(e.target.value, 10) || 1 })}
                        className="w-24 px-2 py-1 border border-border rounded bg-background text-sm tabular-nums" />
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      Hold (min)
                      <input type="number" min="1" max="60" value={Math.round((g.hold_seconds ?? 600) / 60)}
                        onChange={(e) => setGate(slug, { hold_seconds: (parseInt(e.target.value, 10) || 10) * 60 })}
                        className="w-20 px-2 py-1 border border-border rounded bg-background text-sm tabular-nums" />
                    </label>
                  </>
                ) : (
                  <label className="flex items-center gap-2 text-sm">
                    Admit/min
                    <input type="number" min="1" max="100000" value={g.admission_rate}
                      onChange={(e) => setGate(slug, { admission_rate: parseInt(e.target.value, 10) || 1 })}
                      className="w-24 px-2 py-1 border border-border rounded bg-background text-sm tabular-nums" />
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={g.ab_test}
                    onChange={(e) => setGate(slug, { ab_test: e.target.checked })} />
                  A/B test (50% bypass)
                </label>
                {live ? (
                  <span className="text-xs text-muted-foreground tabular-nums ml-auto">
                    {g.mode === 'concurrency'
                      ? `${live.active ?? 0}/${live.max_active ?? g.max_active} active · ${live.waiting} waiting`
                      : `${live.waiting} waiting · ${live.admitted_through} admitted · ${live.joined} joined`}
                  </span>
                ) : (
                  <span className="text-xs text-amber-600 ml-auto">live counters unavailable</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add gate + save */}
      <div className="flex items-center gap-2">
        <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addGate()}
          placeholder="listing slug (e.g. RBNW7Q)"
          className="px-2 py-1 border border-border rounded bg-background text-sm" />
        <button onClick={addGate} className="px-3 py-1 text-sm border border-border rounded hover:bg-accent">Add gate</button>
        <div className="ml-auto flex items-center gap-3">
          {saveMsg && <span className={`text-sm ${saveMsg.startsWith('✗') ? 'text-destructive' : 'text-emerald-600'}`}>{saveMsg}</span>}
          <button onClick={save} disabled={saving}
            className="px-4 py-1.5 text-sm rounded bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? 'Saving…' : 'Save gates'}
          </button>
        </div>
      </div>

      {/* A/B results */}
      {ab && ab.buckets && ab.buckets.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">A/B results (last {ab.days} days)</h3>
          {/* Mobile: bucket cards (the table below is md+ only) */}
          <div className="md:hidden space-y-2">
            {ab.buckets.map((b) => (
              <div key={b.queue_bucket} className="border border-border rounded-md p-3 bg-card text-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium">{b.queue_bucket}</span>
                  <span className="tabular-nums font-medium">{b.conversion_pct}% conv</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span>Sessions: <span className="tabular-nums text-foreground">{b.sessions}</span></span>
                  <span>Carts: <span className="tabular-nums text-foreground">{b.carts}</span></span>
                  <span>Reached pay: <span className="tabular-nums text-foreground">{b.reached_pay}</span></span>
                  <span>Purchased: <span className="tabular-nums text-foreground">{b.purchased}</span></span>
                  <span>Abandoned in line: <span className="tabular-nums text-foreground">{b.queue_bucket === 'queued' && b.abandon_pct != null ? `${b.abandoned}/${b.entered} (${b.abandon_pct}%)` : '—'}</span></span>
                </div>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm border border-border rounded">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="px-3 py-1.5">Bucket</th>
                <th className="px-3 py-1.5 text-right">Sessions</th>
                <th className="px-3 py-1.5 text-right">Carts</th>
                <th className="px-3 py-1.5 text-right">Reached pay</th>
                <th className="px-3 py-1.5 text-right">Purchased</th>
                <th className="px-3 py-1.5 text-right">Conversion</th>
                <th className="px-3 py-1.5 text-right">Abandoned in line</th>
              </tr>
            </thead>
            <tbody>
              {ab.buckets.map((b) => (
                <tr key={b.queue_bucket} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5 font-medium">{b.queue_bucket}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{b.sessions}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{b.carts}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{b.reached_pay}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{b.purchased}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">{b.conversion_pct}%</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {b.queue_bucket === 'queued' && b.abandon_pct != null
                      ? `${b.abandoned}/${b.entered} (${b.abandon_pct}%)` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            queued ≥ bypass conversion → the queue is safe as a demand lever. queued &lt; bypass → it's costing sales.
          </p>
        </div>
      )}
    </div>
  );
}

// Round 84.7: Leads tab — waitlist + queue email captures, failed-code report.
function Leads({ tenantSlug }) {
  const tz = useVenueTz();
  const [view, setView] = useState('captures');
  const [source, setSource] = useState('all');
  const [waitlist, setWaitlist] = useState(null);
  const [codes, setCodes] = useState(null);
  const [abandoned, setAbandoned] = useState(null);
  const [abandonedDays, setAbandonedDays] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/leads/waitlist?source=${source}&days=90`)
      .then((r) => r.json()).then((d) => { if (d.error) throw new Error(d.error); setWaitlist(d); })
      .catch((e) => setError(e.message));
  }, [tenantSlug, source]);

  // Abandoned carts (didn't complete), enriched with last decline. Soft-fail.
  useEffect(() => {
    setAbandoned(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/abandoned-carts?days=${abandonedDays}&limit=200`)
      .then((r) => r.json()).then((d) => !d.error && setAbandoned(d)).catch(() => {});
  }, [tenantSlug, abandonedDays]);

  useEffect(() => {
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/leads/code-failures?days=90`)
      .then((r) => r.json()).then((d) => { if (d.error) throw new Error(d.error); setCodes(d); })
      .catch((e) => setError(e.message));
  }, [tenantSlug]);

  if (error) return <div className="text-destructive">{error}</div>;

  return (
    <div>
      <SubTabs views={LEADS_VIEWS} value={view} onChange={setView} />
      {view === 'captures' && (
      /* Waitlist + queue captures */
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Email captures (last 90 days)
          </h2>
          <div className="flex gap-1">
            {['all', 'waitlist', 'queue'].map((s) => (
              <button key={s} onClick={() => setSource(s)}
                className={`px-2 py-0.5 text-xs rounded border ${source === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-accent'}`}>
                {s === 'all' ? 'All' : s === 'waitlist' ? 'Waitlist' : 'Queue'}
                {waitlist?.by_source?.[s] != null && s !== 'all' ? ` (${waitlist.by_source[s]})` : ''}
              </button>
            ))}
          </div>
          <span className="ml-auto"><CsvButton rows={waitlist?.rows} filename={`${tenantSlug}-leads-${source}.csv`} /></span>
        </div>
        {!waitlist ? <div className="text-muted-foreground text-sm">Loading…</div> : !waitlist.rows.length ? (
          <div className="text-sm text-muted-foreground">No captures in this window.</div>
        ) : (
          <>
          {/* Mobile: capture cards (the table below is md+ only) */}
          <div className="md:hidden space-y-2">
            {waitlist.rows.map((r) => (
              <div key={r.id} className="border border-border rounded-md p-3 bg-card text-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium truncate">{r.email}</span>
                  <span className="font-mono text-xs text-muted-foreground">{shortTime(r.created_at, tz)}</span>
                </div>
                <div className="text-xs">{r.listing_title || '—'}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                  <span className={`px-1.5 py-0.5 rounded ${r.source === 'queue' ? 'bg-amber-500/15 text-amber-600' : 'bg-sky-500/15 text-sky-600'}`}>{r.source}</span>
                  <span>{r.event_start_time ? shortTime(r.event_start_time, tz) : '—'}</span>
                  <span>{[r.ticketmaster_opt_in && 'TM', r.host_opt_in && 'Host'].filter(Boolean).join(', ') || '—'}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border border-border rounded-md overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5">When</th><th className="px-3 py-1.5">Email</th>
                  <th className="px-3 py-1.5">Listing</th><th className="px-3 py-1.5">Event</th>
                  <th className="px-3 py-1.5">Source</th><th className="px-3 py-1.5">Opt-ins</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{shortTime(r.created_at, tz)}</td>
                    <td className="px-3 py-1.5">{r.email}</td>
                    <td className="px-3 py-1.5 text-xs">{r.listing_title || '—'}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">{r.event_start_time ? shortTime(r.event_start_time, tz) : '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${r.source === 'queue' ? 'bg-amber-500/15 text-amber-600' : 'bg-sky-500/15 text-sky-600'}`}>{r.source}</span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">
                      {[r.ticketmaster_opt_in && 'TM', r.host_opt_in && 'Host'].filter(Boolean).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      )}

      {view === 'abandoned' && (
      /* Abandoned carts — didn't complete, enriched with last decline */
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Abandoned carts{abandoned ? ` (${abandoned.total})` : ''}, last {abandonedDays} days
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex flex-wrap gap-1 text-xs">
              {[1, 7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setAbandonedDays(d)}
                  className={`px-2 py-1 rounded-md border ${abandonedDays === d ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}
                >{d}d</button>
              ))}
            </div>
            <CsvButton rows={abandoned?.rows} filename={`${tenantSlug}-abandoned-carts.csv`} />
          </div>
        </div>
        {!abandoned ? <div className="text-muted-foreground text-sm">Loading…</div> : !abandoned.rows.length ? (
          <div className="text-sm text-muted-foreground">No abandoned carts in this range.</div>
        ) : (
          <>
          {/* Mobile: abandoned-cart cards (the table below is md+ only) */}
          <div className="md:hidden space-y-2">
            {abandoned.rows.map((c) => {
              const stageBase = c.status === 'reservation_created' ? 'Order held (unpaid)' : 'Info entered';
              return (
                <div key={c.id} className="border border-border rounded-md p-3 bg-card text-sm">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium truncate">
                      {c.email || '—'}
                      {(c.first_name || c.last_name) && <span className="text-muted-foreground font-normal"> · {[c.first_name, c.last_name].filter(Boolean).join(' ')}</span>}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">{shortTime(c.created_at, tz)}</span>
                  </div>
                  <div className="text-xs">{c.listing_title || '—'}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                    <span>Tix: <span className="tabular-nums text-foreground">{c.num_tickets ?? '—'}</span></span>
                    <span className="tabular-nums">{dollars(c.total_cents)}</span>
                    <span>
                      {stageBase}
                      {c.last_decline && (
                        <span className="text-destructive" title={c.last_decline_message || ''}> · declined: {c.last_decline}</span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border border-border rounded-md overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5">When</th><th className="px-3 py-1.5">Buyer</th>
                  <th className="px-3 py-1.5">Listing</th><th className="px-3 py-1.5 text-right">Tix</th>
                  <th className="px-3 py-1.5 text-right">Total</th><th className="px-3 py-1.5">Stage</th>
                </tr>
              </thead>
              <tbody>
                {abandoned.rows.map((c) => {
                  const stageBase = c.status === 'reservation_created' ? 'Order held (unpaid)' : 'Info entered';
                  return (
                    <tr key={c.id} className="border-t border-border">
                      <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{shortTime(c.created_at, tz)}</td>
                      <td className="px-3 py-1.5 text-xs">
                        {c.email || '—'}
                        {(c.first_name || c.last_name) && <span className="text-muted-foreground"> · {[c.first_name, c.last_name].filter(Boolean).join(' ')}</span>}
                      </td>
                      <td className="px-3 py-1.5 text-xs">{c.listing_title || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.num_tickets ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{dollars(c.total_cents)}</td>
                      <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                        {stageBase}
                        {c.last_decline && (
                          <span className="text-destructive" title={c.last_decline_message || ''}> · declined: {c.last_decline}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      )}

      {view === 'codes' && (
      /* Failed-code report */
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Failed discount / access codes (last 90 days)
        </h2>
        {!codes ? <div className="text-muted-foreground text-sm">Loading…</div> : !codes.grouped.length ? (
          <div className="text-sm text-muted-foreground">No failed code attempts.</div>
        ) : (
          <>
          {/* Mobile: failed-code cards (the table below is md+ only) */}
          <div className="md:hidden space-y-2">
            {codes.grouped.map((g, i) => (
              <div key={i} className="border border-border rounded-md p-3 bg-card text-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono font-medium">{g.code}</span>
                  <span className="tabular-nums font-medium">{g.attempts} attempts</span>
                </div>
                <div className="text-xs text-muted-foreground">{g.result}</div>
                <div className="text-xs text-muted-foreground mt-0.5 break-words">{g.sample_error || '—'}</div>
              </div>
            ))}
          </div>
          <div className="border border-border rounded-md overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5">Code typed</th><th className="px-3 py-1.5">Result</th>
                  <th className="px-3 py-1.5 text-right">Attempts</th><th className="px-3 py-1.5">Sample error</th>
                </tr>
              </thead>
              <tbody>
                {codes.grouped.map((g, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-1.5 font-mono">{g.code}</td>
                    <td className="px-3 py-1.5 text-xs">{g.result}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{g.attempts}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{g.sample_error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      )}
    </div>
  );
}

// Live-sessions table column registry. `def` marks the default-visible set
// (the original table); the rest are opt-in via the Columns modal. The chosen
// set persists per device in localStorage.
const LIVE_COLS = [
  { key: 'started',  label: 'Started',   def: true },
  { key: 'stage',    label: 'Stage',     def: true },
  { key: 'evidence', label: 'Evidence',  def: true },
  { key: 'event',    label: 'Event',     def: true },
  { key: 'cart',     label: 'Cart',      def: true },
  { key: 'tickets',  label: 'Tickets',   def: false },
  { key: 'total',    label: 'Total',     def: true },
  { key: 'timer',    label: 'Timer',     def: true },
  { key: 'ping',     label: 'Last ping', def: false },
  { key: 'email',    label: 'Email',     def: true },
  { key: 'ref',      label: 'Ref',       def: true },
  { key: 'utm',      label: 'UTM',       def: false },
  { key: 'adclick',  label: 'Ad click',  def: false },
  { key: 'discount', label: 'Discount',  def: false },
  { key: 'queue',    label: 'Queue',     def: false },
  { key: 'device_id', label: 'Device ID', def: false },
  { key: 'seen',      label: 'Seen',       def: false },
  { key: 'purchases', label: 'Purchases',  def: false },
  { key: 'automation', label: 'Automation', def: false },
  { key: 'device',   label: 'Browser / OS', def: false },
  { key: 'gpu',      label: 'GPU',        def: false },
  { key: 'storage',  label: 'Storage',    def: false },
  { key: 'network',  label: 'Network',    def: false },
  { key: 'tls',      label: 'TLS',        def: false },
  { key: 'screen',   label: 'Screen',     def: false },
  { key: 'collected', label: 'Collected', def: false },
  { key: 'locale',   label: 'Locale',    def: false },
  { key: 'location', label: 'Location',  def: true },
  { key: 'postal',   label: 'ZIP',       def: false },
  { key: 'isp',      label: 'ISP',       def: false },
  { key: 'ip',       label: 'IP',        def: true },
];
const LIVE_COLS_LS = 'uw:live-cols';
const LIVE_COLS_VERSION = 2;
const LIVE_EVIDENCE_COL_KEYS = new Set([
  'evidence', 'device_id', 'seen', 'purchases', 'automation', 'gpu',
  'storage', 'network', 'tls', 'screen', 'collected',
]);
const defaultLiveCols = () => new Set(LIVE_COLS.filter((c) => c.def).map((c) => c.key));

function saveLiveCols(columns) {
  try {
    localStorage.setItem(LIVE_COLS_LS, JSON.stringify({ version: LIVE_COLS_VERSION, columns: [...columns] }));
  } catch (_e) { /* storage can be blocked */ }
}

// Coarse device summary from the user agent — OS/device plus the browser,
// calling out in-app browsers (Instagram/Facebook/TikTok) since those tell
// the marketing story.
function deviceFromUa(ua) {
  if (!ua) return null;
  const os = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'Mac' : null;
  const br = /Instagram/i.test(ua) ? 'Instagram' : /FBAN|FBAV|FB_IAB/i.test(ua) ? 'Facebook'
    : /TikTok|BytedanceWebview/i.test(ua) ? 'TikTok' : /Edg\//.test(ua) ? 'Edge'
    : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /FxiOS|Firefox/.test(ua) ? 'Firefox'
    : /Safari/.test(ua) ? 'Safari' : null;
  return [os, br].filter(Boolean).join(' · ') || null;
}

function Operations({ tenantSlug, evidenceAllowed }) {
  const tz = useVenueTz();
  const [view, setView] = useState('live');
  const [sessions, setSessions] = useState(null);
  const [logs, setLogs] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [phantoms, setPhantoms] = useState(null);
  const [error, setError] = useState(null);
  const [junoSlug, setJunoSlug] = useState('');
  const [junoBusy, setJunoBusy] = useState(false);
  const [junoMsg, setJunoMsg] = useState(null);
  const [evidenceSession, setEvidenceSession] = useState(null);
  const availableLiveCols = evidenceAllowed
    ? LIVE_COLS
    : LIVE_COLS.filter((column) => !LIVE_EVIDENCE_COL_KEYS.has(column.key));

  // Which live-session columns to show; persisted per device.
  const [liveCols, setLiveCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LIVE_COLS_LS));
      const columns = Array.isArray(saved) ? saved : saved?.columns;
      if (Array.isArray(columns) && columns.length) {
        const migrated = new Set(columns.filter((k) => LIVE_COLS.some((c) => c.key === k)));
        if (!saved?.version || saved.version < LIVE_COLS_VERSION) migrated.add('evidence');
        saveLiveCols(migrated);
        return migrated;
      }
    } catch (_e) { /* corrupt/absent → defaults */ }
    return defaultLiveCols();
  });
  const [colsModal, setColsModal] = useState(false);
  const toggleLiveCol = (k) => setLiveCols((prev) => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    saveLiveCols(n);
    return n;
  });
  const resetLiveCols = () => {
    const d = defaultLiveCols();
    setLiveCols(d);
    saveLiveCols(d);
  };

  // Live in-flight sessions (incl. anonymous pre-info), polled every 10s.
  const loadSessions = () => {
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/live-sessions`)
      .then((r) => r.json()).then((d) => { if (!d.error) setSessions(d); }).catch(() => {});
  };
  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 10000);
    return () => clearInterval(t);
  }, [tenantSlug]);

  const load = () => {
    Promise.all([
      apiFetch(`/api/tenants/${tenantSlug}/universe-widget/logs?level=error&limit=50`).then((r) => r.json()),
      apiFetch(`/api/tenants/${tenantSlug}/reconciliation-alerts`).then((r) => r.json()),
      apiFetch(`/api/tenants/${tenantSlug}/universe-widget/phantoms?days=14`).then((r) => r.json()),
    ]).then(([l, a, p]) => {
      if (l.error || a.error || p.error) throw new Error(l.error || a.error || p.error);
      setLogs(l); setAlerts(a); setPhantoms(p);
    }).catch((e) => setError(e.message));
  };
  useEffect(load, [tenantSlug]);

  // Abandoned carts + raw declines moved to Leads → Abandoned carts (they're
  // leads, enriched with decline reason). Operations is monitoring/health only.

  const runJunoRefresh = async () => {
    if (junoSlug && !confirm(`Refresh juno cache for "${junoSlug}"?`)) return;
    if (!junoSlug && !confirm('Clear the ENTIRE juno cache? This forces a fresh fetch from Universe on the next request for every listing.')) return;
    setJunoBusy(true); setJunoMsg(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/juno-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(junoSlug ? { slug: junoSlug } : {}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setJunoMsg(`Cleared ${d.cleared} entr${d.cleared === 1 ? 'y' : 'ies'} (${d.scope})`);
      setJunoSlug('');
    } catch (e) { setJunoMsg(`✗ ${e.message}`); }
    finally { setJunoBusy(false); setTimeout(() => setJunoMsg(null), 5000); }
  };

  const resolveAlert = async (id, resolution) => {
    const label = { trust_platform: 'Trust platform (update local)', trust_local: 'Trust local (no change)', manual: 'Manual' }[resolution];
    if (!confirm(`Resolve as: ${label}?`)) return;
    const res = await apiFetch(`/api/tenants/${tenantSlug}/reconciliation-alerts/${id}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resolution }),
    });
    if (!res.ok) {
      const d = await res.json();
      alert(`Failed: ${d.error || res.status}`);
      return;
    }
    load();
  };

  if (error) return <div className="text-destructive">{error}</div>;

  return (
    <div>
      <SubTabs views={OPERATIONS_VIEWS} value={view} onChange={setView} />

      {view === 'health' && alerts && alerts.count > 0 && (
        <>
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Reconciliation alerts ({alerts.count}), sold-count drift
          </h2>
          {/* Mobile: alert cards (the table below is md+ only) */}
          <div className="md:hidden space-y-2 mb-6">
            {alerts.alerts.map((a) => (
              <div key={a.id} className="border border-amber-500/40 bg-amber-500/5 rounded-md p-3 text-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium">{a.talent} <span className="text-xs text-muted-foreground">{(a.date || '').slice(0, 10)}</span></span>
                  <span className="font-mono text-xs text-muted-foreground">{shortTime(a.detected_at, tz)}</span>
                </div>
                <div className="text-xs text-muted-foreground">{a.tier_name} · {a.platform}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs mt-1">
                  <span>Local: <span className="tabular-nums">{a.local_sold}</span></span>
                  <span>Platform: <span className="tabular-nums">{a.platform_sold}</span></span>
                  <span>Drift: <span className={`tabular-nums font-medium ${a.drift > 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>{a.drift > 0 ? `+${a.drift}` : a.drift}</span></span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <button onClick={() => resolveAlert(a.id, 'trust_platform')} className="px-1.5 py-0.5 text-[10px] border border-input rounded hover:bg-accent" title="Set local sold_count to match Universe">Trust platform</button>
                  <button onClick={() => resolveAlert(a.id, 'trust_local')} className="px-1.5 py-0.5 text-[10px] border border-input rounded hover:bg-accent" title="Keep local value; ignore platform">Trust local</button>
                  <button onClick={() => resolveAlert(a.id, 'manual')} className="px-1.5 py-0.5 text-[10px] border border-input rounded hover:bg-accent" title="Resolved offline">Manual</button>
                </div>
              </div>
            ))}
          </div>

          <div className="border border-amber-500/40 bg-amber-500/5 rounded-md overflow-x-auto mb-6 hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-amber-500/10 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Detected</th>
                  <th>Event</th>
                  <th>Tier</th>
                  <th>Platform</th>
                  <th className="text-right">Local</th>
                  <th className="text-right">Platform</th>
                  <th className="text-right">Drift</th>
                  <th className="text-right">Resolve</th>
                </tr>
              </thead>
              <tbody>
                {alerts.alerts.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{shortTime(a.detected_at, tz)}</td>
                    <td className="px-3 py-1.5">{a.talent} <span className="text-xs text-muted-foreground">{(a.date || '').slice(0, 10)}</span></td>
                    <td className="px-3 py-1.5 text-xs">{a.tier_name}</td>
                    <td className="px-3 py-1.5 text-xs">{a.platform}</td>
                    <td className="px-3 py-1.5 text-right">{a.local_sold}</td>
                    <td className="px-3 py-1.5 text-right">{a.platform_sold}</td>
                    <td className={`px-3 py-1.5 text-right font-medium ${a.drift > 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>{a.drift > 0 ? `+${a.drift}` : a.drift}</td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => resolveAlert(a.id, 'trust_platform')} className="px-1.5 py-0.5 text-[10px] border border-input rounded hover:bg-accent" title="Set local sold_count to match Universe">Trust platform</button>
                        <button onClick={() => resolveAlert(a.id, 'trust_local')} className="px-1.5 py-0.5 text-[10px] border border-input rounded hover:bg-accent" title="Keep local value; ignore platform">Trust local</button>
                        <button onClick={() => resolveAlert(a.id, 'manual')} className="px-1.5 py-0.5 text-[10px] border border-input rounded hover:bg-accent" title="Resolved offline">Manual</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view === 'live' && (<>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Live sessions ({sessions?.count ?? 0}), active now
        </h2>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setColsModal(true)} className="hidden md:inline-block px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Columns</button>
          <button onClick={loadSessions} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Refresh</button>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mb-3">Auto-refreshes every 10s · in the widget right now · anonymous until the buyer enters their email.</div>
      {/* Mobile: session cards (the table below is md+ only) */}
      <div className="md:hidden space-y-2 mb-6">
        {(sessions?.sessions || []).map((s) => {
          const paid = s.stage === 'PAID';
          const evidence = s.device_evidence;
          const cart = (s.cart_items || []).map((i) => `${i.qty}× ${i.rate_name || '?'}`).join(', ');
          const remainMs = s.cart_expires_at ? s.cart_expires_at - Date.now() : null;
          const timer = paid ? '—' : remainMs == null ? '—' : remainMs <= 0 ? 'expired' : `${Math.floor(remainMs / 60000)}:${String(Math.floor((remainMs % 60000) / 1000)).padStart(2, '0')}`;
          const beatMs = s.updated_at ? Date.now() - new Date(s.updated_at).getTime() : null;
          const beatFresh = beatMs != null && beatMs < 75000;
          const beatTitle = paid ? 'purchased' : beatMs == null ? 'no heartbeat'
            : beatFresh ? `active, last ping ${Math.round(beatMs / 1000)}s ago`
            : `quiet, last ping ${Math.round(beatMs / 1000)}s ago`;
          return (
            <div key={s.session_id} className="border border-border rounded-md p-3 bg-card text-sm">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="flex items-center gap-1.5">
                  <span title={beatTitle} className={`inline-block w-2 h-2 rounded-full ${paid || beatFresh ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`}></span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${paid ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-medium' : 'bg-sky-500/10 text-sky-700 dark:text-sky-300'}`}>{s.stage}</span>
                </span>
                <span className="font-mono text-xs text-muted-foreground">{shortTime(s.created_at, tz)}</span>
              </div>
              <div className="font-medium truncate">{s.listing_title || '—'}</div>
              {evidenceAllowed && (
                <button onClick={() => evidence && setEvidenceSession(s.session_id)} disabled={!evidence} className="mt-1 hover:opacity-80 disabled:cursor-default">
                  <EvidenceBadges evidence={evidence} compact />
                </button>
              )}
              {cart && <div className="text-xs text-muted-foreground truncate">{cart}</div>}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                <span className="tabular-nums">{s.total_cents != null ? dollars(s.total_cents) : '—'}</span>
                <span className={remainMs != null && remainMs <= 0 ? 'text-destructive' : ''}>⏱ {timer}</span>
                <span className="truncate">{s.email || <span className="italic">Anonymous</span>}</span>
                {s.ref && <span className="font-mono">ref:{s.ref}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground mt-0.5">
                <span>{[s.geo_city, s.geo_region].filter(Boolean).join(', ') || '—'}{s.geo_country ? ` ${s.geo_country}` : ''}</span>
                <span className="font-mono">{s.ip || '—'}</span>
              </div>
              {(s.upgrade_accepted === 1 || s.dining_slot_picked === 1 || s.discount_attempted === 1) && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {s.upgrade_accepted === 1 && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">✓ Upgraded</span>}
                  {s.dining_slot_picked === 1 && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">✓ Dining</span>}
                  {s.discount_applied === 1
                    ? <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-mono">✓ {s.discount_code || 'discount'}</span>
                    : s.discount_attempted === 1 && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono" title="code attempted, not applied">tried {s.last_code_tried || 'code'}</span>}
                </div>
              )}
            </div>
          );
        })}
        {sessions && sessions.sessions.length === 0 && <div className="text-center text-muted-foreground py-6 text-sm">No live sessions right now.</div>}
      </div>

      <div className="border border-border rounded-md overflow-x-auto mb-6 hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr className="text-xs uppercase tracking-wide text-muted-foreground">
              {availableLiveCols.filter((c) => liveCols.has(c.key)).map((c) => (
                <th key={c.key} className={`px-3 py-2 font-medium ${['total', 'tickets'].includes(c.key) ? 'text-right' : ''}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(sessions?.sessions || []).map((s) => {
              const paid = s.stage === 'PAID';
              const evidence = s.device_evidence;
              const cart = (s.cart_items || []).map((i) => `${i.qty}× ${i.rate_name || '?'}`).join(', ');
              const remainMs = s.cart_expires_at ? s.cart_expires_at - Date.now() : null;
              const timer = paid ? '—'
                : remainMs == null ? '—'
                : remainMs <= 0 ? 'expired'
                : `${Math.floor(remainMs / 60000)}:${String(Math.floor((remainMs % 60000) / 1000)).padStart(2, '0')}`;
              const beatMs = s.updated_at ? Date.now() - new Date(s.updated_at).getTime() : null;
              const beatFresh = beatMs != null && beatMs < 75000;
              const beatTitle = paid ? 'purchased' : beatMs == null ? 'no heartbeat'
                : beatFresh ? `active, last ping ${Math.round(beatMs / 1000)}s ago`
                : `quiet, last ping ${Math.round(beatMs / 1000)}s ago (live on the hold, but tab not pinging)`;
              const adClick = [s.has_meta_click && 'Meta', s.has_google_click && 'Google', s.has_tiktok_click && 'TikTok'].filter(Boolean).join(' + ');
              const utm = [s.utm_source, s.utm_medium].filter(Boolean).join(' / ');
              const cells = {
                started: <td key="started" className="px-3 py-1.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{shortTime(s.created_at, tz)}</td>,
                stage: (
                  <td key="stage" className="px-3 py-1.5 whitespace-nowrap">
                    <span title={beatTitle} className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${paid || beatFresh ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`}></span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${paid ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-medium' : 'bg-sky-500/10 text-sky-700 dark:text-sky-300'}`}>{s.stage}</span>
                    {s.upgrade_accepted === 1 && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">✓ Upgraded</span>}
                    {s.dining_slot_picked === 1 && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">✓ Dining</span>}
                  </td>
                ),
                evidence: (
                  <td key="evidence" className="px-3 py-1.5 whitespace-nowrap">
                    <button onClick={() => evidence && setEvidenceSession(s.session_id)} disabled={!evidence} className="hover:opacity-80 disabled:cursor-default">
                      <EvidenceBadges evidence={evidence} compact />
                    </button>
                  </td>
                ),
                event: <td key="event" className="px-3 py-1.5 truncate max-w-[14rem]">{s.listing_title || '—'}</td>,
                cart: <td key="cart" className="px-3 py-1.5 text-xs max-w-[16rem] truncate" title={cart}>{cart || <span className="text-muted-foreground">—</span>}</td>,
                tickets: <td key="tickets" className="px-3 py-1.5 text-right tabular-nums">{s.num_tickets ?? <span className="text-muted-foreground">—</span>}</td>,
                total: <td key="total" className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{s.total_cents != null ? dollars(s.total_cents) : '—'}</td>,
                timer: <td key="timer" className={`px-3 py-1.5 text-xs tabular-nums whitespace-nowrap ${!paid && remainMs != null && remainMs <= 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{timer}</td>,
                ping: <td key="ping" className="px-3 py-1.5 text-xs tabular-nums whitespace-nowrap text-muted-foreground">{beatMs == null ? '—' : `${Math.round(beatMs / 1000)}s ago`}</td>,
                email: <td key="email" className="px-3 py-1.5 text-muted-foreground">{s.email || <span className="italic">Anonymous</span>}</td>,
                ref: <td key="ref" className="px-3 py-1.5 text-xs font-mono whitespace-nowrap">{s.ref || <span className="text-muted-foreground">—</span>}</td>,
                utm: <td key="utm" className="px-3 py-1.5 text-xs whitespace-nowrap" title={s.utm_campaign || ''}>{utm || <span className="text-muted-foreground">—</span>}</td>,
                adclick: <td key="adclick" className="px-3 py-1.5 text-xs whitespace-nowrap">{adClick || <span className="text-muted-foreground">—</span>}</td>,
                discount: (
                  <td key="discount" className="px-3 py-1.5 text-xs whitespace-nowrap">
                    {s.discount_applied ? <span className="text-emerald-600 dark:text-emerald-400 font-mono">✓ {s.discount_code || 'applied'}</span>
                      : s.discount_attempted ? <span className="text-muted-foreground" title="attempted, not applied">tried{s.last_code_tried ? <span className="font-mono"> {s.last_code_tried}</span> : ''}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                ),
                queue: (
                  <td key="queue" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground">
                    {s.queue_bucket ? `${s.queue_bucket}${s.queue_wait_seconds != null ? ` · ${s.queue_wait_seconds}s` : ''}` : '—'}
                  </td>
                ),
                device_id: <td key="device_id" className="px-3 py-1.5 font-mono text-xs whitespace-nowrap"><button onClick={() => evidence && setEvidenceSession(s.session_id)} disabled={!evidence} className="hover:underline disabled:no-underline">{evidence?.device_alias || <span className="text-muted-foreground">—</span>}</button></td>,
                seen: <td key="seen" className="px-3 py-1.5 text-right tabular-nums">{evidence?.related_sessions ?? <span className="text-muted-foreground">—</span>}</td>,
                purchases: <td key="purchases" className="px-3 py-1.5 text-right tabular-nums">{evidence?.related_purchases ?? <span className="text-muted-foreground">—</span>}</td>,
                automation: <td key="automation" className="px-3 py-1.5 text-xs whitespace-nowrap">{evidence?.automation_flags?.length ? <span className="text-amber-700 dark:text-amber-300">{evidence.automation_flags.join(', ')}</span> : <span className="text-muted-foreground">None observed</span>}</td>,
                device: <td key="device" className="px-3 py-1.5 text-xs whitespace-nowrap" title={s.user_agent || ''}>{evidence?.browser_os || deviceFromUa(s.user_agent) || <span className="text-muted-foreground">—</span>}</td>,
                gpu: <td key="gpu" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground">{evidence?.gpu || '—'}</td>,
                storage: <td key="storage" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground">{evidence?.storage || '—'}</td>,
                network: <td key="network" className="px-3 py-1.5 text-xs whitespace-nowrap" title={evidence?.organization || ''}>{[evidence?.network, evidence?.asn].filter(Boolean).join(' · ') || <span className="text-muted-foreground">—</span>}</td>,
                tls: <td key="tls" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground">{evidence?.tls || '—'}</td>,
                screen: <td key="screen" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground">{evidence?.screen || '—'}</td>,
                collected: <td key="collected" className="px-3 py-1.5 font-mono text-xs whitespace-nowrap text-muted-foreground">{evidence?.collected_at ? shortTime(evidence.collected_at, tz) : '—'}</td>,
                locale: <td key="locale" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground">{s.locale || '—'}</td>,
                location: (
                  <td key="location" className="px-3 py-1.5 text-xs whitespace-nowrap" title={s.geo_isp || ''}>
                    {[s.geo_city, s.geo_region].filter(Boolean).join(', ') || <span className="text-muted-foreground">—</span>}
                    {s.geo_country && <span className="ml-1 text-muted-foreground">{s.geo_country}</span>}
                  </td>
                ),
                postal: <td key="postal" className="px-3 py-1.5 text-xs font-mono whitespace-nowrap text-muted-foreground">{s.geo_postal || '—'}</td>,
                isp: <td key="isp" className="px-3 py-1.5 text-xs whitespace-nowrap text-muted-foreground max-w-[10rem] truncate" title={s.geo_isp || ''}>{s.geo_isp || '—'}</td>,
                ip: <td key="ip" className="px-3 py-1.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{s.ip || '—'}</td>,
              };
              return (
                <tr key={s.session_id} className="border-t border-border">
                  {availableLiveCols.filter((c) => liveCols.has(c.key)).map((c) => cells[c.key])}
                </tr>
              );
            })}
            {sessions && sessions.sessions.length === 0 && <tr><td colSpan={availableLiveCols.filter((column) => liveCols.has(column.key)).length} className="px-3 py-6 text-center text-muted-foreground">No live sessions right now.</td></tr>}
          </tbody>
        </table>
      </div>

      {colsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setColsModal(false)} />
          <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-sm p-4">
            <h2 className="font-semibold mb-1">Live session columns</h2>
            <p className="text-xs text-muted-foreground mb-3">Choose which columns the table shows. Saved on this device.</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
              {availableLiveCols.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={liveCols.has(c.key)} onChange={() => toggleLiveCol(c.key)} className="accent-primary" />
                  {c.label}
                </label>
              ))}
            </div>
            <div className="flex justify-between">
              <button onClick={resetLiveCols} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent">Reset to defaults</button>
              <button onClick={() => setColsModal(false)} className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90">Done</button>
            </div>
          </div>
        </div>
      )}
      </>)}

      {view === 'health' && (<>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
        Recent widget errors ({logs?.count || 0})
      </h2>
      {/* Mobile: error cards (the table below is md+ only) */}
      <div className="md:hidden space-y-2">
        {logs?.logs.map((l) => (
          <div key={l.id} className="border border-border rounded-md p-3 bg-card text-sm">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-muted-foreground">{l.step || '—'}</span>
              <span className="font-mono text-xs text-muted-foreground">{shortTime(l.created_at, tz)}</span>
            </div>
            <div className="text-destructive break-words">{l.message}</div>
          </div>
        ))}
        {(!logs?.logs || logs.logs.length === 0) && <div className="text-center text-muted-foreground py-6 text-sm">No errors logged.</div>}
      </div>

      <div className="border border-border rounded-md overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr><th className="px-3 py-2 font-medium">When</th><th>Step</th><th>Message</th></tr>
          </thead>
          <tbody>
            {logs?.logs.map((l) => (
              <tr key={l.id} className="border-t border-border">
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{shortTime(l.created_at, tz)}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{l.step || '—'}</td>
                <td className="px-3 py-1.5 truncate max-w-2xl text-destructive">{l.message}</td>
              </tr>
            ))}
            {(!logs?.logs || logs.logs.length === 0) && <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">No errors logged.</td></tr>}
          </tbody>
        </table>
      </div>

      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2 mt-6">
        Phantom-purchase suspects ({phantoms?.count || 0}), last 14 days
      </h2>
      <div className="text-[11px] text-muted-foreground mb-2">
        Payment attempts that failed AND the buyer never came back. May represent recoverable customers (decline → bailed) or actual phantoms (paid but our DB missed it).
      </div>
      {/* Mobile: phantom cards (the table below is md+ only) */}
      <div className="md:hidden space-y-2 mb-6">
        {(phantoms?.phantoms || []).slice(0, 20).map((p) => (
          <div key={p.id} className="border border-border rounded-md p-3 bg-card text-sm">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-medium">{[p.first_name, p.last_name].filter(Boolean).join(' ') || '(no name)'}</span>
              <span className="font-mono text-xs text-muted-foreground">{shortTime(p.created_at, tz)}</span>
            </div>
            {p.email && <div className="text-xs text-muted-foreground">{p.email}</div>}
            <div className="text-xs truncate">{p.listing_title || '—'}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
              <span>Decline: <span className="font-mono">{p.decline_code || '—'}</span></span>
              <span>{p.card_brand && p.card_last4 ? `${p.card_brand} ····${p.card_last4}` : '—'}</span>
              <span className="tabular-nums">{dollars(p.total_cents)}</span>
            </div>
            {p.decline_message && <div className="text-[10px] text-muted-foreground mt-0.5 break-words">{p.decline_message}</div>}
          </div>
        ))}
        {(!phantoms?.phantoms || phantoms.phantoms.length === 0) && (
          <div className="text-center text-muted-foreground py-6 text-sm">No phantom suspects in the last 14 days.</div>
        )}
        {phantoms?.count > 20 && (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            Showing 20 of {phantoms.count}, full export via the audit-phantom-purchases.js script.
          </div>
        )}
      </div>

      <div className="border border-border rounded-md overflow-x-auto mb-6 hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Buyer</th>
              <th className="px-3 py-2 font-medium">Event</th>
              <th className="px-3 py-2 font-medium">Decline</th>
              <th className="px-3 py-2 font-medium">Card</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(phantoms?.phantoms || []).slice(0, 20).map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{shortTime(p.created_at, tz)}</td>
                <td className="px-3 py-1.5">
                  <div>{[p.first_name, p.last_name].filter(Boolean).join(' ') || '(no name)'}</div>
                  <div className="text-[10px] text-muted-foreground">{p.email || ''}</div>
                </td>
                <td className="px-3 py-1.5 truncate max-w-xs">{p.listing_title || '—'}</td>
                <td className="px-3 py-1.5 text-xs">
                  <span className="font-mono">{p.decline_code || '—'}</span>
                  {p.decline_message && <div className="text-[10px] text-muted-foreground truncate max-w-xs">{p.decline_message}</div>}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{p.card_brand && p.card_last4 ? `${p.card_brand} ····${p.card_last4}` : '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{dollars(p.total_cents)}</td>
              </tr>
            ))}
            {(!phantoms?.phantoms || phantoms.phantoms.length === 0) && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No phantom suspects in the last 14 days.</td></tr>
            )}
          </tbody>
        </table>
        {phantoms?.count > 20 && (
          <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/30 border-t border-border">
            Showing 20 of {phantoms.count}, full export via the audit-phantom-purchases.js script.
          </div>
        )}
      </div>
      </>)}

      {view === 'maintenance' && (
      <div>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
        Juno cache refresh
      </h2>
      <div className="border border-border rounded-md p-3 bg-card">
        <div className="text-xs text-muted-foreground mb-2">
          Clears universe-widget's in-memory cache of Universe juno responses. Use after editing a listing in Universe's seller admin if the widget is serving stale data.
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={junoSlug}
            onChange={(e) => setJunoSlug(e.target.value)}
            placeholder="optional: listing slug (leave blank to clear all)"
            className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
          />
          <button
            onClick={runJunoRefresh}
            disabled={junoBusy}
            className="px-3 py-1 text-xs border border-primary text-primary rounded-md hover:bg-primary/10 disabled:opacity-50"
          >{junoBusy ? 'Clearing…' : (junoSlug ? 'Refresh one' : 'Clear all')}</button>
        </div>
        {junoMsg && <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{junoMsg}</div>}
      </div>
      </div>
      )}
      <EvidenceDrawer tenantSlug={tenantSlug} sessionId={evidenceSession} onClose={() => setEvidenceSession(null)} />
    </div>
  );
}

function WidgetConfig({ tenantSlug }) {
  const tz = useVenueTz();
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);

  const reload = () => {
    setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setCfg(d); })
      .catch((e) => setError(e.message));
  };
  useEffect(reload, [tenantSlug]);

  if (error) return <div className="text-destructive">Error: {error}</div>;
  if (!cfg) return <div className="text-muted-foreground">Loading…</div>;

  const dining = cfg.dining || {};
  const groups = [
    {
      icon: '📐', title: 'Branding & appearance',
      items: [
        { id: 'branding', label: 'Branding' },
        { id: 'chat',     label: 'Chat widget' },
      ],
    },
    {
      icon: '🍽', title: 'Dining',
      items: [
        { id: 'dining',       label: 'Display' },
        { id: 'venues',       label: 'Venues' },
        { id: 'dining-mode',  label: 'Integration mode' },
        { id: 'skip-list',    label: 'Skip list' },
      ],
    },
    {
      icon: '📊', title: 'Tracking & attribution',
      items: [
        { id: 'pixels', label: 'Marketing pixels' },
        { id: 'capi',   label: 'CAPI tokens' },
      ],
    },
    {
      icon: '🔒', title: 'Security & access',
      items: [
        { id: 'domains',     label: 'Domain allowlist' },
        { id: 'partner-api', label: 'partner_api' },
        { id: 'stylesheets', label: 'Allowed stylesheets' },
      ],
    },
  ];
  const mod = cfg.modified_at ? new Date(cfg.modified_at) : null;
  return (
    <div className="flex gap-6">
      <aside className="hidden lg:block w-48 flex-shrink-0">
        <nav className="sticky top-4 text-sm">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-2">Jump to</div>
          {groups.map((g) => (
            <div key={g.title} className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold px-2 mb-1">{g.title}</div>
              <div className="space-y-0.5">
                {g.items.map((s) => (
                  <a
                    key={s.id}
                    href={`#section-${s.id}`}
                    className="block px-2 py-0.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground text-xs"
                  >{s.label}</a>
                ))}
              </div>
            </div>
          ))}
          {mod && (
            <div className="mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground leading-tight">
              <div>Last saved</div>
              <div className="font-mono">{formatStamp(cfg.modified_at, tz)}</div>
            </div>
          )}
        </nav>
      </aside>
      <div className="flex-1 max-w-3xl space-y-6">
        <GroupHeader icon="📐" title="Branding & appearance" />
        <div id="section-branding"><BrandingSection tenantSlug={tenantSlug} branding={cfg.branding || {}} onSaved={reload} /></div>
        <div id="section-chat"><ChatSection tenantSlug={tenantSlug} chat={cfg.chat || {}} onSaved={reload} /></div>

        <GroupHeader icon="🍽" title="Dining" />
        <div id="section-dining"><DiningDisplaySection tenantSlug={tenantSlug} dining={dining} onSaved={reload} /></div>
        <div id="section-venues"><DiningVenuesSection tenantSlug={tenantSlug} venues={dining.venues || []} onSaved={reload} /></div>
        <div id="section-dining-mode"><DiningModeSection dining={dining} /></div>
        <div id="section-skip-list"><DiningSkipSection tenantSlug={tenantSlug} list={dining.skip_for_listings || []} onSaved={reload} /></div>

        <GroupHeader icon="📊" title="Tracking & attribution" />
        <div id="section-pixels"><PixelsSection tenantSlug={tenantSlug} pixels={cfg.extra_pixels || {}} onSaved={reload} /></div>
        <div id="section-capi"><CapiTokensSection tenantSlug={tenantSlug} status={cfg.sensitive_status || {}} onSaved={reload} /></div>

        <GroupHeader icon="🔒" title="Security & access" />
        <div id="section-domains"><DomainsSection tenantSlug={tenantSlug} domains={cfg.domains || []} onSaved={reload} /></div>
        <div id="section-partner-api"><PartnerApiSection tenantSlug={tenantSlug} status={cfg.sensitive_status || {}} onSaved={reload} /></div>
        <div id="section-stylesheets"><AllowedStylesheetsSection tenantSlug={tenantSlug} list={cfg.allowed_stylesheets || []} onSaved={reload} /></div>

        <div className="text-xs text-muted-foreground pt-2 border-t border-border">
          Edits write atomically to <span className="font-mono">host-config.json</span> + trigger an in-memory reload, buyers see new values on their next page load (no PM2 restart).
        </div>
      </div>
    </div>
  );
}

function GroupHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-2 pt-4 border-t-2 border-border first:border-t-0 first:pt-0">
      <span className="text-lg leading-none">{icon}</span>
      <h3 className="text-base font-semibold">{title}</h3>
    </div>
  );
}

function BrandingSection({ tenantSlug, branding, onSaved }) {
  const [draft, setDraft] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(branding);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/branding`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // Live preview — show a mock "Buy tickets" button + step header bar
  // using the current draft colors. No save required to see it.
  const btn = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(draft.buttonColor || '') ? draft.buttonColor : null;
  const prim = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(draft.primaryColor || '') ? draft.primaryColor : null;

  return (
    <section>
      <SectionHeader title="Branding" dirty={dirty} saving={saving} savedAt={savedAt} err={err} onSave={save} />
      <div className="border border-border rounded-md p-4 bg-card grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <ColorField label="Button color" value={draft.buttonColor || ''} onChange={(v) => setDraft({ ...draft, buttonColor: v })} />
        <ColorField label="Primary color" value={draft.primaryColor || ''} onChange={(v) => setDraft({ ...draft, primaryColor: v })} />
        <div className="col-span-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!draft.disableBookingProtect}
              onChange={(e) => setDraft({ ...draft, disableBookingProtect: e.target.checked })}
            />
            <span className="text-sm">Disable Booking Protect upsell (XCover)</span>
          </label>
        </div>
        <div className="col-span-2 mt-2">
          <div className="text-xs text-muted-foreground mb-2">Live preview</div>
          <div className="border border-border rounded-md overflow-hidden">
            <div className="px-3 py-2 text-xs text-white" style={{ background: prim || '#0a0a0a' }}>
              Step 1 of 3 · Tickets
            </div>
            <div className="bg-background p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">General Admission</div>
                <div className="text-xs text-muted-foreground">$45 + fees</div>
              </div>
              <button
                type="button"
                className="px-4 py-2 text-sm text-white rounded-md"
                style={{ background: btn || '#999' }}
              >Buy tickets</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChatSection({ tenantSlug, chat, onSaved }) {
  const editable = {
    enabled:     !!chat.enabled,
    provider:    chat.provider || '',
    brand_id:    chat.brand_id || '',
    script_url:  chat.script_url || '',
  };
  const [draft, setDraft] = useState(editable);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(editable);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/chat`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <section>
      <SectionHeader title="Chat widget" dirty={dirty} saving={saving} savedAt={savedAt} err={err} onSave={save} />
      <div className="border border-border rounded-md p-4 bg-card grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <label className="flex items-center gap-2 col-span-2">
          <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
          <div>
            <div>Chat widget enabled</div>
            <div className="text-xs text-muted-foreground">Loaded inside the ticket modal so buyers can ask questions without losing focus to the host page.</div>
          </div>
        </label>
        <div>
          <TextField label="Provider" value={draft.provider} onChange={(v) => setDraft({ ...draft, provider: v })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">Identifies which chat platform, e.g. <span className="font-mono">teliance-messaging</span>.</div>
        </div>
        <div>
          <TextField label="Brand ID" value={draft.brand_id} onChange={(v) => setDraft({ ...draft, brand_id: v })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">Brand-scoping key sent to the chat backend.</div>
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground mb-1 block">Script URL</label>
          <input
            type="text"
            value={draft.script_url}
            onChange={(e) => setDraft({ ...draft, script_url: e.target.value })}
            className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
          />
          <div className="text-[11px] text-muted-foreground mt-0.5">Public URL of the chat widget script, loaded inside the iframe.</div>
        </div>
      </div>
    </section>
  );
}

function DiningVenuesSection({ tenantSlug, venues, onSaved }) {
  const [draft, setDraft] = useState(venues);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => setDraft(venues), [venues]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(venues);

  const update = (i, patch) => setDraft((d) => d.map((v, idx) => idx === i ? { ...v, ...patch } : v));
  const remove = (i) => {
    if (!confirm(`Remove venue "${draft[i].name}" from the dining card?`)) return;
    setDraft((d) => d.filter((_, idx) => idx !== i));
  };
  const moveUp = (i) => i > 0 && setDraft((d) => {
    const out = d.slice(); [out[i-1], out[i]] = [out[i], out[i-1]]; return out;
  });
  const moveDown = (i) => i < draft.length-1 && setDraft((d) => {
    const out = d.slice(); [out[i], out[i+1]] = [out[i+1], out[i]]; return out;
  });
  const add = () => setDraft((d) => [...d, { id: '', name: '', cuisine: '', image_url: '', subdomain: '', partner_venue_id: '', avg_spend_per_cover: 0 }]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/dining-venues`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venues: draft }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <section>
      <SectionHeader title={`Dining venues (${draft.length})`} dirty={dirty} saving={saving} savedAt={savedAt} err={err} onSave={save} />
      <div className="space-y-2">
        {draft.map((v, i) => {
          const isNew = !venues.find((existing) => existing.id === v.id);
          return (
            <div key={i} className="border border-border rounded-md p-3 bg-card">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-xs font-medium text-muted-foreground">
                  #{i + 1} {v.name || '(unnamed)'}{isNew && <span className="ml-1 text-emerald-600 dark:text-emerald-400">[NEW]</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveUp(i)} disabled={i === 0} className="px-1.5 py-0.5 text-xs border border-input rounded hover:bg-accent disabled:opacity-40">↑</button>
                  <button onClick={() => moveDown(i)} disabled={i === draft.length-1} className="px-1.5 py-0.5 text-xs border border-input rounded hover:bg-accent disabled:opacity-40">↓</button>
                  <button onClick={() => remove(i)} className="px-2 py-0.5 text-xs border border-destructive text-destructive rounded hover:bg-destructive/10">Remove</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <TextField label={isNew ? "ID (slug; lowercase, no spaces)" : "ID (immutable)"} value={v.id || ''} onChange={isNew ? ((x) => update(i, { id: x })) : undefined} />
                <TextField label="Name" value={v.name || ''} onChange={(x) => update(i, { name: x })} />
                <TextField label="Cuisine" value={v.cuisine || ''} onChange={(x) => update(i, { cuisine: x })} />
                <TextField label="Subdomain" value={v.subdomain || ''} onChange={(x) => update(i, { subdomain: x })} />
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">Image URL</label>
                  <input
                    type="text"
                    value={v.image_url || ''}
                    onChange={(e) => update(i, { image_url: e.target.value })}
                    className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
                  />
                </div>
                <NumField label="Avg spend per cover ($)" value={v.avg_spend_per_cover ?? 0} onChange={(x) => update(i, { avg_spend_per_cover: x })} min={0} max={2000} />
                <TextField label={isNew ? "SR partner_venue_id" : "SR partner_venue_id (immutable)"} value={v.partner_venue_id || ''} onChange={isNew ? ((x) => update(i, { partner_venue_id: x })) : undefined} />
              </div>
            </div>
          );
        })}
      </div>
      <button onClick={add} className="mt-2 px-3 py-1 text-xs border border-primary text-primary rounded-md hover:bg-primary/10">+ New venue</button>
    </section>
  );
}

function DiningModeSection({ dining }) {
  return (
    <section>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Dining integration mode <span className="text-[10px] normal-case font-normal">(read-only, locked at integration)</span></h2>
      <div className="border border-border rounded-md p-3 bg-card grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div><div className="text-xs text-muted-foreground">Provider</div><div className="font-mono">{dining.provider || '—'}</div></div>
        <div><div className="text-xs text-muted-foreground">Mode</div><div className="font-mono">{dining.mode || '—'}</div></div>
        <div><div className="text-xs text-muted-foreground">Submit mode</div><div className="font-mono">{dining.submit_mode || '—'}</div></div>
      </div>
    </section>
  );
}

function PixelsSection({ tenantSlug, pixels, onSaved }) {
  const editable = {
    fb_pixel_id:        pixels.fb_pixel_id || '',
    tiktok_pixel_id:    pixels.tiktok_pixel_id || '',
    ga4_measurement_id: pixels.ga4_measurement_id || '',
  };
  const [draft, setDraft] = useState(editable);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(editable);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body = {
        fb_pixel_id:        draft.fb_pixel_id.trim() || null,
        tiktok_pixel_id:    draft.tiktok_pixel_id.trim() || null,
        ga4_measurement_id: draft.ga4_measurement_id.trim() || null,
      };
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/pixels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <section>
      <SectionHeader title="Marketing pixels" dirty={dirty} saving={saving} savedAt={savedAt} err={err} onSave={save} />
      <div className="border border-border rounded-md p-4 bg-card grid grid-cols-1 gap-3 text-sm">
        <div>
          <TextField label="Facebook pixel ID" value={draft.fb_pixel_id} onChange={(v) => setDraft({ ...draft, fb_pixel_id: v })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">Long numeric ID from Meta Events Manager.</div>
        </div>
        <div>
          <TextField label="TikTok pixel ID" value={draft.tiktok_pixel_id} onChange={(v) => setDraft({ ...draft, tiktok_pixel_id: v })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">Hex ID from TikTok Events Manager.</div>
        </div>
        <div>
          <TextField label="GA4 Measurement ID" value={draft.ga4_measurement_id} onChange={(v) => setDraft({ ...draft, ga4_measurement_id: v })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">Starts with <span className="font-mono">G-</span>.</div>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Changing a pixel ID renames the key in <span className="font-mono">capi.&lt;channel&gt;</span> and preserves the existing token. To rotate the token itself, use CAPI tokens below.
        </div>
      </div>
    </section>
  );
}

function DomainsSection({ tenantSlug, domains, onSaved }) {
  const [items, setItems] = useState(domains);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => setItems(domains), [domains]);

  const run = async (body) => {
    setBusy(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/domains`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setItems(d.domains || []);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Domain allowlist ({items.length})
        </h2>
        <div className="text-xs flex items-center gap-2">
          {err && <span className="text-destructive">{err}</span>}
          {savedAt && Date.now() - savedAt < 3000 && <span className="text-emerald-600 dark:text-emerald-400">✓ Saved</span>}
        </div>
      </div>
      <div className="border border-amber-500/40 bg-amber-500/10 rounded-md p-2 mb-2 text-xs text-amber-700 dark:text-amber-300">
        ⚠ Removing a domain prevents the widget from loading on that hostname. Don't remove a domain ops is using.
      </div>
      <div className="border border-border rounded-md p-3 bg-card">
        <div className="space-y-1 mb-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">No entries.</div>}
          {items.map((x) => (
            <div key={x} className="flex items-center gap-2">
              <span className="font-mono text-xs flex-1">{x}</span>
              <button
                onClick={() => {
                  if (!confirm(`Remove "${x}" from the domain allowlist? The widget will stop loading on this hostname.`)) return;
                  run({ remove: [x] });
                }}
                disabled={busy}
                className="px-2 py-0.5 text-xs border border-destructive text-destructive rounded-md hover:bg-destructive/10 disabled:opacity-50"
              >Remove</button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={add}
            onChange={(e) => setAdd(e.target.value)}
            placeholder="hostname (e.g. www.livnightclub.com)"
            className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
          />
          <button
            onClick={() => { if (add.trim()) { run({ add: [add.trim()] }); setAdd(''); } }}
            disabled={busy || !add.trim()}
            className="px-3 py-1 text-xs border border-primary text-primary rounded-md hover:bg-primary/10 disabled:opacity-50"
          >+ Add</button>
        </div>
      </div>
    </section>
  );
}

function CapiTokensSection({ tenantSlug, status, onSaved }) {
  const fields = [
    { bodyKey: 'fb_token',       label: 'Meta CAPI token (Facebook server-side)', statusKey: 'capi_fb' },
    { bodyKey: 'tiktok_token',   label: 'TikTok CAPI token (server-side)',         statusKey: 'capi_tiktok' },
    { bodyKey: 'ga4_api_secret', label: 'GA4 Measurement Protocol API secret',     statusKey: 'capi_ga4' },
  ];
  return (
    <section>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
        Server-side CAPI tokens <span className="text-[10px] normal-case font-normal">(write-only, Replace rotates without revealing the current value)</span>
      </h2>
      <div className="border border-border rounded-md overflow-hidden">
        {fields.map((f) => (
          <SecretRow
            key={f.bodyKey}
            label={f.label}
            state={status[f.statusKey]}
            endpoint={`/api/tenants/${tenantSlug}/universe-widget/config/capi`}
            bodyKey={f.bodyKey}
            minLen={16}
            onSaved={onSaved}
          />
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">
        CAPI tokens are stored under <span className="font-mono">capi.&lt;channel&gt;[&lt;pixel_id&gt;]</span>. The pixel ID comes from Marketing pixels above. Set it first if not configured.
      </div>
    </section>
  );
}

function PartnerApiSection({ tenantSlug, status, onSaved }) {
  const fieldStatus = status.partner_api_fields || {};
  const fields = [
    { bodyKey: 'client_id',      label: 'SR partner client_id',     minLen: 4 },
    { bodyKey: 'client_secret',  label: 'SR partner client_secret', minLen: 16 },
    { bodyKey: 'base_url',       label: 'SR partner base_url',      minLen: 8 },
    { bodyKey: 'booked_by',      label: 'booked_by label',          minLen: 1 },
    { bodyKey: 'internal_notes', label: 'Default internal_notes',   minLen: 0 },
  ];
  return (
    <section>
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
        SevenRooms partner_api <span className="text-[10px] normal-case font-normal">(write-only, used to book dinners on behalf of buyers)</span>
      </h2>
      <div className="border border-border rounded-md overflow-hidden">
        {fields.map((f) => (
          <SecretRow
            key={f.bodyKey}
            label={f.label}
            state={fieldStatus[f.bodyKey]}
            endpoint={`/api/tenants/${tenantSlug}/universe-widget/config/partner-api`}
            bodyKey={f.bodyKey}
            minLen={f.minLen}
            onSaved={onSaved}
          />
        ))}
      </div>
    </section>
  );
}

function SecretRow({ label, state, endpoint, bodyKey, minLen, onSaved }) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'editing'
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const configured = state === 'configured';

  const submit = async (action) => {
    setBusy(true); setErr(null);
    try {
      const body = { [bodyKey]: action === 'clear' ? null : value };
      const res = await apiFetch(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setSavedAt(Date.now());
      setMode('idle'); setValue(''); setShow(false);
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-3 py-2 border-t border-border first:border-t-0">
      <div className="flex items-center gap-3">
        <div className="flex-1 text-sm">{label}</div>
        {configured ? (
          <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">configured ✓</span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">not configured</span>
        )}
        {savedAt && Date.now() - savedAt < 3000 && (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">✓ Saved</span>
        )}
        {mode === 'idle' && (
          <>
            <button
              onClick={() => { setMode('editing'); setValue(''); setShow(false); setErr(null); }}
              className="px-2 py-0.5 text-xs border border-input rounded-md hover:bg-accent"
            >{configured ? 'Replace' : 'Set'}</button>
            {configured && (
              <button
                onClick={() => {
                  if (confirm(`Clear ${label}? This is irreversible without the original value.`)) submit('clear');
                }}
                disabled={busy}
                className="px-2 py-0.5 text-xs border border-destructive text-destructive rounded-md hover:bg-destructive/10 disabled:opacity-50"
              >Clear</button>
            )}
          </>
        )}
      </div>
      {mode === 'editing' && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={minLen > 0 ? `new value (min ${minLen} chars)` : 'new value'}
            className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
            autoFocus
          />
          <button onClick={() => setShow(!show)} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent" title={show ? 'Hide' : 'Show'}>{show ? '🙈' : '👁'}</button>
          <button
            onClick={() => submit('rotate')}
            disabled={busy || value.trim().length < minLen}
            className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
          >{busy ? 'Saving…' : 'Save'}</button>
          <button
            onClick={() => { setMode('idle'); setValue(''); setShow(false); setErr(null); }}
            disabled={busy}
            className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
          >Cancel</button>
        </div>
      )}
      {err && <div className="mt-1 text-xs text-destructive">{err}</div>}
    </div>
  );
}

function AllowedStylesheetsSection({ tenantSlug, list, onSaved }) {
  const [items, setItems] = useState(list);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => setItems(list), [list]);

  const run = async (body) => {
    setBusy(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/allowed-stylesheets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setItems(d.allowed_stylesheets || []);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Allowed stylesheet origins ({items.length})
        </h2>
        <div className="text-xs flex items-center gap-2">
          {err && <span className="text-destructive">{err}</span>}
          {savedAt && Date.now() - savedAt < 3000 && <span className="text-emerald-600 dark:text-emerald-400">✓ Saved</span>}
        </div>
      </div>
      <div className="border border-border rounded-md p-3 bg-card">
        <div className="text-xs text-muted-foreground mb-2">
          Origins from which custom CSS may be loaded into the widget. Listed in the widget's CSP frame.
        </div>
        <div className="space-y-1 mb-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">No entries.</div>}
          {items.map((x) => (
            <div key={x} className="flex items-center gap-2">
              <span className="font-mono text-xs flex-1 truncate">{x}</span>
              <button
                onClick={() => { if (confirm(`Remove ${x} from allowed stylesheets?`)) run({ remove: [x] }); }}
                disabled={busy}
                className="px-2 py-0.5 text-xs border border-destructive text-destructive rounded-md hover:bg-destructive/10 disabled:opacity-50"
              >Remove</button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={add}
            onChange={(e) => setAdd(e.target.value)}
            placeholder="https://example.com (origin URL)"
            className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
          />
          <button
            onClick={() => { if (add.trim()) { run({ add: [add.trim()] }); setAdd(''); } }}
            disabled={busy || !add.trim()}
            className="px-3 py-1 text-xs border border-primary text-primary rounded-md hover:bg-primary/10 disabled:opacity-50"
          >+ Add</button>
        </div>
      </div>
    </section>
  );
}

function DiningDisplaySection({ tenantSlug, dining, onSaved }) {
  const editable = {
    enabled: !!dining.enabled,
    heading: dining.heading || '',
    subheading: dining.subheading || '',
    compact_times: !!dining.compact_times,
    include_request_only: !!dining.include_request_only,
    default_party_size: dining.default_party_size ?? 2,
    max_visible_times: dining.max_visible_times ?? 6,
    halo_size_interval: dining.halo_size_interval ?? 15,
  };
  const [draft, setDraft] = useState(editable);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(editable);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body = {
        ...draft,
        default_party_size: Number(draft.default_party_size),
        max_visible_times:  Number(draft.max_visible_times),
        halo_size_interval: Number(draft.halo_size_interval),
      };
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/dining-display`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <section>
      <SectionHeader title="Dining display" dirty={dirty} saving={saving} savedAt={savedAt} err={err} onSave={save} />
      <div className="border border-border rounded-md p-4 bg-card grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <label className="flex items-center gap-2 col-span-2">
          <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
          <span>Dining cross-sell enabled</span>
        </label>
        <TextField label="Heading" value={draft.heading} onChange={(v) => setDraft({ ...draft, heading: v })} />
        <TextField label="Subheading" value={draft.subheading} onChange={(v) => setDraft({ ...draft, subheading: v })} />
        <NumField label="Default party size" min={1} max={20} value={draft.default_party_size} onChange={(v) => setDraft({ ...draft, default_party_size: v })} hint="Initial party size shown when the dining card appears." />
        <NumField label="Max visible time slots" min={1} max={50} value={draft.max_visible_times} onChange={(v) => setDraft({ ...draft, max_visible_times: v })} hint="Initial slot count per venue; more shown after 'See all times'." />
        <NumField label="Halo interval (min)" min={5} max={120} value={draft.halo_size_interval} onChange={(v) => setDraft({ ...draft, halo_size_interval: v })} hint="SR availability lookahead window, e.g. 15 = ±15 min from the show time." />
        <label className="flex items-center gap-2 col-span-2">
          <input type="checkbox" checked={draft.compact_times} onChange={(e) => setDraft({ ...draft, compact_times: e.target.checked })} />
          <div>
            <div>Compact time-slot layout</div>
            <div className="text-xs text-muted-foreground">Tighter spacing between time pills, useful when there are many slots.</div>
          </div>
        </label>
        <label className="flex items-center gap-2 col-span-2">
          <input type="checkbox" checked={draft.include_request_only} onChange={(e) => setDraft({ ...draft, include_request_only: e.target.checked })} />
          <div>
            <div>Include request-only slots</div>
            <div className="text-xs text-muted-foreground">Show times that require manual venue confirmation (no instant book).</div>
          </div>
        </label>
      </div>
    </section>
  );
}

function DiningSkipSection({ tenantSlug, list, onSaved }) {
  const [items, setItems] = useState(list);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => setItems(list), [list]);

  const run = async (body) => {
    setBusy(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/config/dining-skip`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || `http ${res.status}`);
      setItems(d.skip_for_listings || []);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Dining skip list ({items.length})
        </h2>
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          {err && <span className="text-destructive">{err}</span>}
          {savedAt && Date.now() - savedAt < 3000 && <span className="text-emerald-600 dark:text-emerald-400">✓ Saved</span>}
        </div>
      </div>
      <div className="border border-border rounded-md p-3 bg-card">
        <div className="text-xs text-muted-foreground mb-3">
          Listings where the dining cross-sell stays hidden. Match against Universe listing.id or slug (e.g. <span className="font-mono">8W6CRS</span>).
        </div>
        <div className="space-y-1 mb-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">No entries.</div>}
          {items.map((x) => (
            <div key={x} className="flex items-center gap-2">
              <span className="font-mono text-xs flex-1">{x}</span>
              <button
                onClick={() => run({ remove: [x] })}
                disabled={busy}
                className="px-2 py-0.5 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
              >Remove</button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={add}
            onChange={(e) => setAdd(e.target.value)}
            placeholder="listing id or slug…"
            className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
          />
          <button
            onClick={() => { if (add.trim()) { run({ add: [add.trim()] }); setAdd(''); } }}
            disabled={busy || !add.trim()}
            className="px-3 py-1 text-xs border border-primary text-primary rounded-md hover:bg-primary/10 disabled:opacity-50"
          >+ Add</button>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ title, dirty, saving, savedAt, err, onSave }) {
  const showSaved = savedAt && Date.now() - savedAt < 3000;
  return (
    <div className="flex items-baseline justify-between mb-2">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{title}</h2>
      <div className="flex items-center gap-2 text-xs">
        {err && <span className="text-destructive">{err}</span>}
        {showSaved && !dirty && <span className="text-emerald-600 dark:text-emerald-400">✓ Saved</span>}
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="px-3 py-1 border border-primary text-primary rounded-md hover:bg-primary/10 disabled:opacity-50 disabled:border-input disabled:text-muted-foreground"
        >{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  const isValid = !value || /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-12 border border-input rounded-md bg-background cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#RRGGBB"
          className={`flex-1 px-2 py-1 border rounded-md bg-background text-sm font-mono ${isValid ? 'border-input' : 'border-destructive text-destructive'}`}
        />
      </div>
    </div>
  );
}
function TextField({ label, value, onChange }) {
  const readOnly = !onChange;
  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <input
        type="text"
        value={value}
        readOnly={readOnly}
        onChange={readOnly ? undefined : ((e) => onChange(e.target.value))}
        className={`w-full px-2 py-1 border border-input rounded-md text-sm ${readOnly ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-background'}`}
      />
    </div>
  );
}
function NumField({ label, value, onChange, min, max, hint }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <input
        type="number"
        min={min} max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 border border-input rounded-md bg-background text-sm"
      />
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function Stat({ label, value, variant }) {
  const tone = variant === 'destructive' ? 'text-destructive' : 'text-foreground';
  return (
    <div className="border border-border rounded-md p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${tone}`}>{value?.toLocaleString() ?? '—'}</div>
    </div>
  );
}
